import { Component, ElementRef, EventEmitter, Inject, Injectable, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';
import { DialogConfirmComponent } from '../../../shared/components/dialogs/dialog-confirm/dialog-confirm.component';
import { XmlPreviewDialogComponent } from '../../../../components/dialog-windows/dialog-xml-preview/dialog-xml-preview.component';
import { Observable, forkJoin, ReplaySubject, Subject, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { XTAInstanceDTO } from '../../models/xta-instance.model';
import { BautagebuchService } from '../../services/bautagebuch.service';
import {
  BautagebuchDataspaceAssetSelectionDto,
  BautagebuchDataspaceImportPreviewDto,
  BautagebuchImportedSourceDto,
  BautagebuchImportedWorkerSummaryDto,
  BautagebuchStatus,
  DataspaceAssetResponseDto,
  DataspaceCatalogItemDto,
  DataspacePolicyTemplateDto,
  DataspacePullResponseDto
} from '../../models/bautagebuch.models';
import { OrganisationService } from '../../../../services/dataServices/organisation-service/organisationservice';
import { OrganisationInterface } from '../../../../model/organisations/organisation-interface';
import { UserService } from '../../../../services/user-service/userservice';

const BAUTAGEBUCH_DATE_FORMATS = {
  parse: {
    dateInput: 'input'
  },
  display: {
    dateInput: 'input',
    monthYearLabel: { month: 'short', year: 'numeric' },
    dateA11yLabel: { day: '2-digit', month: '2-digit', year: 'numeric' },
    monthYearA11yLabel: { month: 'long', year: 'numeric' }
  }
};

@Injectable()
class BautagebuchDateAdapter extends NativeDateAdapter {
  parse(value: unknown): Date | null {
    if (typeof value === 'string') {
      const normalized = value.trim();
      if (!normalized) {
        return null; 
      }

      const dotSeparatedMatch = normalized.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      if (dotSeparatedMatch) {
        const day = Number(dotSeparatedMatch[1]);
        const month = Number(dotSeparatedMatch[2]) - 1;
        const year = Number(dotSeparatedMatch[3]);
        const parsed = new Date(year, month, day);

        return parsed.getFullYear() === year && parsed.getMonth() === month && parsed.getDate() === day
          ? parsed
          : new Date(NaN); 
      }

      return new Date(NaN); 
    }

    if (value instanceof Date) {
      return value;
    }
    return null;
  }

  format(date: Date, displayFormat: string): string {
    if (displayFormat === 'input') {
      const day = `${date.getDate()}`.padStart(2, '0');
      const month = `${date.getMonth() + 1}`.padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    }

    return super.format(date, displayFormat);
  }
}

interface MessageTypeConfig {
  title: string;
  description: string;
}

interface PageConfig {
  title: string;
  sectionPath: string;
  formGroupName: string;
  isRepeatable?: boolean;
  minInstances?: number;
  maxInstances?: number;
  readOnly?: boolean;  
  lockedInstances?: boolean; 
}

interface MarkerLinkedMedia {
  id: string;
  type: 'image' | 'video';
  title: string;
  src: string;
  thumbnailSrc?: string;
  description?: string;
}

interface FloorplanMarkerConfig {
  id: string;
  label: string;
  x: number;
  y: number;
  media: MarkerLinkedMedia[];
}

interface FloorplanAssetConfig {
  imageSrc: string;
  originalWidth: number;
  originalHeight: number;
  markers: FloorplanMarkerConfig[];
}

const MESSAGE_TYPE_CONFIGS: { [key: string]: MessageTypeConfig } = {
  'wegesicherung.antrag.2002': {
    title: 'Antrag auf Zustimmung nach § 127 TKG (XBreitband-Nachricht Nr. 2002)',
    description: 'Die Nachricht stellt einen Antrag auf Zustimmung zur Verlegung/Änderung einer TK-Linie nach § 127 TKG dar. Die Nachricht enthält die Daten des Antrags und Metadaten der zu übertragenden Anlagen.'
  },
  'wegesicherung.korrekturAntrag.2004': {
    title: 'Korrektur des Antrags (XBreitband-Nachricht Nr. 2004)',
    description: 'Reaktion auf Mängelliste mit geänderten oder ergänzten Daten des Antrags'
  },
  'wegesicherung.nachbesserungAntrag.2006': {
    title: 'Nachbesserung des Antrags (XBreitband-Nachricht Nr. 2006)',
    description: 'Reaktion auf Änderungsbedarf (2005) mit Stellungnahmen zu den Befunden'
  }
};

@Component({
  standalone: false,
  selector: 'app-bautagebuch-legacy-form',
  templateUrl: './bautagebuch-legacy-form.component.html',
  styleUrls: ['./bautagebuch-legacy-form.component.css'],
  providers: [
    { provide: DateAdapter, useClass: BautagebuchDateAdapter },
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
    { provide: MAT_DATE_FORMATS, useValue: BAUTAGEBUCH_DATE_FORMATS }
  ]
})
export class BautagebuchLegacyFormComponent implements OnInit, OnDestroy {
  private static readonly BAUTAGEBUCH_API_BASE_URL = '/proxy/api/v0/bautagebuchservice/api/v1';
  private static readonly BAUTAGEBUCH_MESSAGE_TYPE = 'bautagebuch.unified.0001';
  private static readonly BAUTAGEBUCH_SECTION_PATH = 'bautagebuch-simplified';
  private static readonly HIDDEN_BAUTAGEBUCH_OPTIONAL_SECTION_IDS = new Set(['personal', 'nachunternehmer']);
  private static readonly TAGESBERICHT_NU_MESSAGE_TYPE = 'tagesbericht.nachunternehmer.0001';
  private static readonly TAGESBERICHT_NU_SECTION_PATH = 'tagesbericht-nu-simplified';
  private static readonly NU_UI_META_KEY = '__uiMeta';
  private static readonly NU_FINALISED_STATUS = 'FINALISIERT';
  private static readonly GU_AUTO_IMPORT_SECTION_IDS = new Set(['project', 'day', 'weather']);
  private static readonly BAUTAGEBUCH_FLOORPLAN: FloorplanAssetConfig = {
    imageSrc: 'assets/bautagebuch/grundriss-4og.jpg',
    originalWidth: 1490,
    originalHeight: 931,
    markers: [
      {
        id: 'door',
        label: 'Eingangstür',
        x: 750,
        y: 650,
        media: [
          {
            id: 'door-image',
            type: 'image',
            title: 'Eingangstür',
            src: 'assets/bautagebuch/door.jpg',
            thumbnailSrc: 'assets/bautagebuch/door.jpg',
            description: 'Die Eingangstür wurde montiert'
          }
        ]
      }
    ]
  };

  messageType: string;  
  sectionPath: string;  
  sectionTitle: string; 

  private static readonly GERAETE_STATUS_OPTIONS: BautagebuchOptionDTO[] = [
    { value: 'IN_BETRIEB', label: 'In Betrieb' },
    { value: 'BEREITSCHAFT', label: 'In Bereitschaft' },
    { value: 'DEFEKT', label: 'Defekt' },
    { value: 'ABGEMELDET', label: 'Abgemeldet' }
  ];  caseId?: string;      
  mode: 'new' | 'edit' | 'preview' = 'new';
  draftId?: string;
  destinationId?: string;
  serviceType?: string;
  serviceName?: string;
  existingFieldValues?: {[key: string]: any};

  inReplyToMessageId?: string;
  inReplyToMessageType?: string;

  @Output() xmlGenerated = new EventEmitter<any>();

  form: FormGroup;
  fieldDefinitions: FieldDefinitionDTO[] = [];
  loading = false;
  nuDraftSaveInProgress = false;
  error: string | null = null;
  private initialFormValue: any;
  private lastSavedResponse: any = null;  

  isMultiPage: boolean = false;
  messageTypeConfig?: MessageTypeConfig;
  pages: PageConfig[] = [];
  allFieldDefinitions: Map<string, FieldDefinitionDTO[]> = new Map();
  currentPageIndex: number = 0;

  isTagesberichtNUMode: boolean = false;
  tagesberichtNUSections: BautagebuchSectionsResponseDTO | null = null;
  tagesberichtNUTemplates: BautagebuchTemplateDTO[] = [];
  tagesberichtNUTemplateId: string | null = null;
  tagesberichtNUActiveOptionalSections: BautagebuchOptionalSectionViewModel[] = [];
  tagesberichtNUOptionalCatalogGroups: BautagebuchSectionGroupDTO[] = [];
  nuActiveOptionalSectionId: string | null = null;
  nuBehinderungControls: FormGroup[] = [];
  nuDataspaceAsset: DataspaceAssetResponseDto | null = null;
  nuDataspaceBusy: boolean = false;

  isBautagebuchMode: boolean = false;
  bautagebuchViewMode: 'quick' | 'schema' = 'quick';
  bautagebuchSchemaTree: XTAInstanceDTO | null = null;
  bautagebuchSchemaLoading: boolean = false;
  bautagebuchSchemaError: string | null = null;
  bautagebuchSchemaPage: number = 0;
  bautagebuchSchemaSections: BautagebuchSchemaSectionViewModel[] = [];
  bautagebuchSchemaFieldValues: {[sectionId: string]: {[fieldName: string]: any}} = {};
  bautagebuchSections: BautagebuchSectionsResponseDTO | null = null;
  bautagebuchTemplates: BautagebuchTemplateDTO[] = [];
  bautagebuchTemplateId: string | null = null;
  bautagebuchSearch: string = '';
  bautagebuchOptionalCatalogGroups: BautagebuchSectionGroupDTO[] = [];
  bautagebuchActiveOptionalSections: BautagebuchOptionalSectionViewModel[] = [];
  companyReportEntries: BautagebuchCompanyReportViewModel[] = [];
  weatherMeasurementControls: FormGroup[] = [];
  showProjectEditor: boolean = false;
  showWeatherEditor: boolean = false;
  activeOptionalSectionId: string | null = null;
  bearbeiterSummaryName: string = '';
  projectSummaryTitle: string = '';
  projectSummaryMeta: string = '';
  weatherSummaryTitle: string = '';
  weatherSummaryMeta: string = '';
  weatherSummaryIcon: string = 'wb_sunny';
  isFloorplanOverlayOpen: boolean = false;
  dataspaceCatalogItems: DataspaceCatalogItemDto[] = [];
  dataspaceCatalogLoading: boolean = false;
  dataspaceCatalogProjectKey: string = '';
  dataspaceCatalogReportDate: string = '';
  dataspaceSelectedAssetIds: string[] = [];
  dataspaceAssetSectionSelections: {[assetId: string]: string[]} = {};
  dataspacePreview: DataspacePullResponseDto | null = null;
  dataspaceImportPreview: BautagebuchDataspaceImportPreviewDto | null = null;
  dataspaceImportBusy: boolean = false;
  bautagebuchImportedSources: BautagebuchImportedSourceDto[] = [];
  readonly floorplanConfig: FloorplanAssetConfig = BautagebuchLegacyFormComponent.BAUTAGEBUCH_FLOORPLAN;
  selectedFloorplanMarkerId: string = this.floorplanConfig.markers[0]?.id || '';
  datumPickerControl = new FormControl(null as Date | null);
  readonly workerQualificationOptions: BautagebuchOptionDTO[] = [
    { value: 'POLIER', label: 'Polier' },
    { value: 'VORARBEITER', label: 'Vorarbeiter' },
    { value: 'FACHARBEITER', label: 'Facharbeiter' },
    { value: 'HILFSARBEITER', label: 'Hilfsarbeiter' }
  ];

  readonly workerLohngruppeOptions: BautagebuchOptionDTO[] = [
    { value: 'A1', label: 'A1' },
    { value: 'A2', label: 'A2' },
    { value: 'A3', label: 'A3' },
    { value: 'A4', label: 'A4' },
    { value: 'B1', label: 'B1' },
    { value: 'B2', label: 'B2' },
    { value: 'SONSTIGE', label: 'Sonstige' }
  ];

  readonly weatherOptions: BautagebuchOptionDTO[] = [
    { value: 'SONNIG', label: 'Sonnig' },
    { value: 'BEWOELKT', label: 'Bewölkt' },
    { value: 'REGEN', label: 'Regen' },
    { value: 'SCHNEE', label: 'Schnee' },
    { value: 'WINDIG', label: 'Windig' }
  ];
  readonly customWorkerOptionValue: string = '__custom__';

  public registergerichtFilterCtrl: FormControl = new FormControl();
  public filteredOptions: ReplaySubject<CodeDTO[]> = new ReplaySubject<CodeDTO[]>(1);
  public weatherFilterCtrl: FormControl = new FormControl();
  public filteredWeatherOptions: ReplaySubject<BautagebuchOptionDTO[]> = new ReplaySubject<BautagebuchOptionDTO[]>(1);
  public dataspaceConsumerFilterCtrl: FormControl = new FormControl();
  public filteredDataspaceConsumerOrganisations: ReplaySubject<OrganisationInterface[]> = new ReplaySubject<OrganisationInterface[]>(1);
  protected _onDestroy = new Subject<void>();
  private bautagebuchViewBindings$ = new Subject<void>();
  private workerCustomFields = new WeakMap<FormGroup, { qualifikation?: boolean; lohngruppe?: boolean }>();
  @ViewChildren('scrollableContent') private scrollableContents!: QueryList<ElementRef<HTMLElement>>;
  activeOrganisationId: number | null = null;
  activeOrganisationName: string = '';
  availableDataspaceConsumerOrganisations: OrganisationInterface[] = [];
  previewVisibleNuSectionIds: string[] | null = null;

  get isEditMode(): boolean {
    return this.mode === 'edit' || !!this.draftId;
  }

  get isReadOnlyPreview(): boolean {
    return this.mode === 'preview';
  }

  get hasUnsavedChanges(): boolean {
    return JSON.stringify(this.form.value) !== JSON.stringify(this.initialFormValue);
  }

  get bautagebuchDateControl(): FormControl {
    return this.form.get('day.datum') as FormControl;
  }

  get isNuUiBusy(): boolean {
    return this.loading || this.nuDraftSaveInProgress;
  }

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private bautagebuchService: BautagebuchService,
    private organisationService: OrganisationService,
    private userService: UserService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<BautagebuchLegacyFormComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit(): void {
    this.setupWeatherFilter();
    this.setupDataspaceConsumerFilter();
    this.applyActiveOrganisation(this.organisationService.activeOrganisation);
    if (this.data?.organisationId) {
      this.activeOrganisationId = this.data.organisationId;
    }
    if (this.data?.organisationName) {
      this.activeOrganisationName = this.data.organisationName;
    }
    this.organisationService.activeItemObserver
      .pipe(takeUntil(this._onDestroy))
      .subscribe((organisation) => this.applyActiveOrganisation(organisation));
    this.resolveFallbackOrganisation();
    this.messageType = this.data?.messageType;
    this.caseId = this.data.caseId;
    this.mode = this.data.mode || 'new';
    this.draftId = this.data.draftId;
    this.destinationId = this.data.destinationId;
    this.serviceType = this.data.serviceType;
    this.serviceName = this.data.serviceName;
    this.existingFieldValues = this.data.existingFieldValues;
    this.previewVisibleNuSectionIds = Array.isArray(this.data?.previewVisibleSectionIds)
      ? [...this.data.previewVisibleSectionIds]
      : null;

    this.inReplyToMessageId = this.data.inReplyToMessageId;
    this.inReplyToMessageType = this.data.inReplyToMessageType;

    this.applyWorkspaceFallbacks();

    if (this.messageType === BautagebuchLegacyFormComponent.TAGESBERICHT_NU_MESSAGE_TYPE) {
      this.loadDataspaceOrganisationOptions();
      this.isTagesberichtNUMode = true;
      this.setupTagesberichtNUMode();
      return;
    }

    if (this.messageType === BautagebuchLegacyFormComponent.BAUTAGEBUCH_MESSAGE_TYPE) {
      this.loadDataspaceOrganisationOptions();
      this.isBautagebuchMode = true;
      this.setupBautagebuchMode();
      return;
    }

    if (this.messageType === 'wegesicherung.antrag.2002' ||
        this.messageType === 'wegesicherung.korrekturAntrag.2004' ||
        this.messageType === 'wegesicherung.nachbesserungAntrag.2006') {
      this.isMultiPage = true;
      this.setupMultiPageWizard();
    } else {
      this.sectionPath = this.data.sectionPath;
      this.sectionTitle = this.data.sectionTitle;
      this.loadFormMetadata();
      this.initialFormValue = this.form.value; 
    }
  }

  private applyWorkspaceFallbacks(): void {
    if (!this.messageType && this.data?.workspaceKey === 'tagesbericht-nu') {
      this.messageType = BautagebuchLegacyFormComponent.TAGESBERICHT_NU_MESSAGE_TYPE;
      this.sectionPath = this.sectionPath || BautagebuchLegacyFormComponent.TAGESBERICHT_NU_SECTION_PATH;
      this.sectionTitle = this.sectionTitle || 'Tagesbericht (Nachunternehmer)';
      this.serviceType = this.serviceType || 'tagesbericht-nu';
      this.serviceName = this.serviceName || 'Tagesbericht NU';
    }

    if (!this.messageType && this.data?.workspaceKey === 'bautagebuch-gu') {
      this.messageType = BautagebuchLegacyFormComponent.BAUTAGEBUCH_MESSAGE_TYPE;
      this.sectionPath = this.sectionPath || BautagebuchLegacyFormComponent.BAUTAGEBUCH_SECTION_PATH;
      this.sectionTitle = this.sectionTitle || 'Bautagebuch';
      this.serviceType = this.serviceType || 'bautagebuch-gu';
      this.serviceName = this.serviceName || 'Bautagebuch';
    }

    if (!this.sectionPath) {
      if (this.messageType === BautagebuchLegacyFormComponent.TAGESBERICHT_NU_MESSAGE_TYPE) {
        this.sectionPath = BautagebuchLegacyFormComponent.TAGESBERICHT_NU_SECTION_PATH;
      } else if (this.messageType === BautagebuchLegacyFormComponent.BAUTAGEBUCH_MESSAGE_TYPE) {
        this.sectionPath = BautagebuchLegacyFormComponent.BAUTAGEBUCH_SECTION_PATH;
      }
    }

    if (!this.sectionTitle) {
      if (this.messageType === BautagebuchLegacyFormComponent.TAGESBERICHT_NU_MESSAGE_TYPE) {
        this.sectionTitle = 'Tagesbericht (Nachunternehmer)';
      } else if (this.messageType === BautagebuchLegacyFormComponent.BAUTAGEBUCH_MESSAGE_TYPE) {
        this.sectionTitle = 'Bautagebuch';
      }
    }
  }

  private applyReadOnlyPreviewState(): void {
    if (!this.isReadOnlyPreview || !this.form) {
      return;
    }

    this.form.disable({ emitEvent: false });
    this.showProjectEditor = false;
    this.showWeatherEditor = false;
  }

  loadFormMetadata(): void {
    this.loading = true;
    this.error = null;

    const url = this.getMetadataUrl();
    const params = { sectionPath: this.sectionPath };

    this.http.get<FilteredMetadataDTO>(url, { params }).subscribe({
      next: (metadata) => {
        this.fieldDefinitions = metadata.fields;
        this.fieldDefinitions.forEach(field => {
          if (field.codeListUrn?.includes('registergerichte')) {
            this.setupRegistergerichtFilter(field);
          }
        });

        this.initializeForm();
        this.loading = false;
      },
      error: (error) => {
        this.error = `Fehler beim Laden der Formulardaten: ${error.message || 'Unbekannter Fehler'}`;
        this.snackBar.open(this.error, 'Schließen', {
          duration: 5000,
          panelClass: ['error-snackbar']
        });
        this.loading = false;
      }
    });
  }

  private setupTagesberichtNUMode(): void {
    this.loading = true;
    this.error = null;

    this.http.get<BautagebuchSectionsResponseDTO>(
      this.getSectionsUrl()
    ).subscribe({
      next: (sections) => {
        this.tagesberichtNUSections = sections;
        const applyMergedValues = (sourceValues?: any): void => {
          const normalizedSourceValues = this.normalizeTagesberichtNUFieldValues(sourceValues || {});
          const mergedValues = this.deepMerge(sections.defaultValues || {}, normalizedSourceValues);

          if (!mergedValues.day?.datum) {
            if (!mergedValues.day) { mergedValues.day = {}; }
            mergedValues.day.datum = new Date().toISOString().slice(0, 10);
          }

          this.initializeTagesberichtNUForm(mergedValues);
          this.initialFormValue = this.form.getRawValue();
          this.loading = false;
          this.loadTagesberichtNUTemplates();
          this.loadCurrentNUDataspaceAsset();
        };

        if (this.draftId && !this.existingFieldValues && !this.isReadOnlyPreview) {
          this.bautagebuchService.getBautagebuchSubmission(
            this.draftId,
            this.getRequiredOrganisationId()
          ).subscribe({
            next: (draft) => {
              this.existingFieldValues = draft?.fieldValues || {};
              applyMergedValues(this.existingFieldValues);
            },
            error: () => {
              applyMergedValues(this.existingFieldValues || {});
            }
          });
          return;
        }

        applyMergedValues(this.existingFieldValues || {});
      },
      error: (error) => {
        this.loading = false;
        this.error = `Fehler beim Laden des Tagesberichts: ${error.message || 'Unbekannter Fehler'}`;
      }
    });
  }

  private normalizeTagesberichtNUFieldValues(sourceValues: any): any {
    if (!sourceValues || typeof sourceValues !== 'object' || Array.isArray(sourceValues)) {
      return sourceValues || {};
    }

    const normalized = this.cloneTagesberichtNUFieldValues(sourceValues);
    const optionalSections = normalized.optionalSections;
    if (optionalSections && typeof optionalSections === 'object' && !Array.isArray(optionalSections)) {
      Object.keys(optionalSections).forEach((sectionId) => {
        if (normalized[sectionId] == null) {
          normalized[sectionId] = this.cloneTagesberichtNUFieldValues(optionalSections[sectionId]);
        }
      });
    }

    return normalized;
  }

  private cloneTagesberichtNUFieldValues(value: any): any {
    if (value == null) {
      return value;
    }
    return JSON.parse(JSON.stringify(value));
  }

  private initializeTagesberichtNUForm(values: any): void {
    const previewSelectedSectionIds = this.isReadOnlyPreview && this.previewVisibleNuSectionIds?.length
      ? [...this.previewVisibleNuSectionIds]
      : null;
    this.form = this.fb.group({
      selectedSectionIds: [previewSelectedSectionIds || values.selectedSectionIds || ['project', 'day', 'weather', 'nuFirma', 'ausgefuehrteArbeiten', 'behinderungen', 'interneFelder']],
      project: this.fb.group({
        bezeichnung: [values.project?.bezeichnung || ''],
        generalunternehmer: [values.project?.generalunternehmer || ''],
        nachunternehmer: [values.project?.nachunternehmer || ''],
        gewerk: [values.project?.gewerk || '']
      }),
      day: this.fb.group({
        datum: [values.day?.datum || ''],
        vorarbeiter: [values.day?.vorarbeiter || ''],
        arbeitszeit: this.fb.group({
          beginn: [values.day?.arbeitszeit?.beginn || '07:00'],
          ende: [values.day?.arbeitszeit?.ende || '17:00']
        })
      }),
      weather: this.fb.group({
        tageslage: [values.weather?.tageslage || 'SONNIG'],
        temperaturMinC: [values.weather?.temperaturMinC ?? 0],
        temperaturMaxC: [values.weather?.temperaturMaxC ?? 14],
        bemerkung: [values.weather?.bemerkung || ''],
        messungen: this.fb.array((values.weather?.messungen || []).map((e: any) => this.createWeatherMeasurementGroup(e)))
      }),
      nuFirma: this.fb.group({
        firma: this.fb.group({
          name: [values.nuFirma?.firma?.name || '']
        }),
        arbeitszeit: this.fb.group({
          beginn: [values.nuFirma?.arbeitszeit?.beginn || '07:00'],
          ende: [values.nuFirma?.arbeitszeit?.ende || '17:00']
        }),
        schlechtesWetter: this.fb.group({
          beginn: [values.nuFirma?.schlechtesWetter?.beginn || ''],
          ende: [values.nuFirma?.schlechtesWetter?.ende || '']
        }),
        arbeitskraefte: this.fb.array((values.nuFirma?.arbeitskraefte || []).map((w: any) => this.createWorkerGroup(w)))
      }),
      ausgefuehrteArbeiten: this.fb.array((values.ausgefuehrteArbeiten || []).map((e: any) => this.createNUOptionalItemGroup('ausgefuehrteArbeiten', e))),
      behinderungen: this.fb.array((values.behinderungen || []).map((e: any) => this.createNUBehinderungGroup(e))),
      geraete: this.fb.array((values.geraete || []).map((e: any) => this.createOptionalItemGroup('geraete', e))),
      material: this.fb.array((values.material || []).map((e: any) => this.createNUMaterialGroup(e))),
      weisungen: this.fb.array((values.weisungen || []).map((e: any) => this.createNUWeisungGroup(e))),
      pruefungen: this.fb.array((values.pruefungen || []).map((e: any) => this.createOptionalItemGroup('pruefungen', e))),
      verdeckteLeistungen: this.fb.array((values.verdeckteLeistungen || []).map((e: any) => this.createOptionalItemGroup('verdeckteLeistungen', e))),
      vorkommnisse: this.fb.array((values.vorkommnisse || []).map((e: any) => this.createOptionalItemGroup('vorkommnisse', e))),
      interneFelder: this.fb.group({
        interneZuordnung: [values.interneFelder?.interneZuordnung || ''],
        sollIstLeistung: [values.interneFelder?.sollIstLeistung || ''],
        produktiveZeitVerlustzeit: [values.interneFelder?.produktiveZeitVerlustzeit || ''],
        internerVorgangsbezug: [values.interneFelder?.internerVorgangsbezug || ''],
        interneBewertungHinweis: [values.interneFelder?.interneBewertungHinweis || '']
      })
    });

    if (this.getNUArbeitskraefte().length === 0) {
      this.getNUArbeitskraefte().push(this.createWorkerGroup({}));
    }

    this.datumPickerControl.setValue(this.parseBautagebuchDate(values.day?.datum), { emitEvent: false });
    this.form.get('weather')?.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => this.refreshWeatherSummary());

    this.refreshTagesberichtNUViewState();
    this.applyReadOnlyPreviewState();
  }

  private createNUBehinderungGroup(entry: any): FormGroup {
    return this.fb.group({
      beginn: [entry?.beginn || ''],
      ende: [entry?.ende || ''],
      ursache: [entry?.ursache || ''],
      betroffenerBereich: [entry?.betroffenerBereich || ''],
      betroffenePersonalAnzahl: [entry?.betroffenePersonalAnzahl ?? ''],
      ausweichmassnahme: [entry?.ausweichmassnahme || '']
    });
  }

  private createNUMaterialGroup(entry: any): FormGroup {
    return this.fb.group({
      materialOderBauteil: [entry?.materialOderBauteil || ''],
      menge: [entry?.menge || ''],
      mengeneinheit: [entry?.mengeneinheit || ''],
      lieferscheinNr: [entry?.lieferscheinNr || ''],
      notiz: [entry?.notiz || '']
    });
  }

  private createNUWeisungGroup(entry: any): FormGroup {
    return this.fb.group({
      zeitpunkt: [entry?.zeitpunkt || ''],
      angewiesenVon: [entry?.angewiesenVon || ''],
      inhalt: [entry?.inhalt || '']
    });
  }

  private createNUOptionalItemGroup(sectionId: string, entry: any): FormGroup {
    if (sectionId === 'ausgefuehrteArbeiten') {
      return this.fb.group({
        beschreibung: [entry?.beschreibung || ''],
        arbeitsfortschritt: [entry?.arbeitsfortschritt || ''],
        sonstiges: [entry?.sonstiges || '']
      });
    }
    return this.createOptionalItemGroup(sectionId, entry);
  }

  getNUArbeitskraefte(): FormArray {
    return this.form.get('nuFirma.arbeitskraefte') as FormArray;
  }

  addNUWorker(): void {
    this.getNUArbeitskraefte().push(this.createWorkerGroup({}));
  }

  removeNUWorker(index: number): void {
    this.getNUArbeitskraefte().removeAt(index);
  }

  getNUBehinderungen(): FormArray {
    return this.form.get('behinderungen') as FormArray;
  }

  addNUBehinderungEntry(): void {
    this.getNUBehinderungen().push(this.createNUBehinderungGroup({}));
    this.refreshNUBehinderungControls();
  }

  removeNUBehinderungEntry(index: number): void {
    this.getNUBehinderungen().removeAt(index);
    this.refreshNUBehinderungControls();
  }

  getNUAusgefuehrteArbeiten(): FormArray {
    return this.form.get('ausgefuehrteArbeiten') as FormArray;
  }

  getNUGeraete(): FormArray {
    return this.form.get('geraete') as FormArray;
  }

  getNUMaterial(): FormArray {
    return this.form.get('material') as FormArray;
  }

  getNUWeisungen(): FormArray {
    return this.form.get('weisungen') as FormArray;
  }

  addNUAusgefuehrteArbeitenEntry(): void {
    this.getNUAusgefuehrteArbeiten().push(this.createNUOptionalItemGroup('ausgefuehrteArbeiten', {}));
  }

  removeNUAusgefuehrteArbeitenEntry(index: number): void {
    this.getNUAusgefuehrteArbeiten().removeAt(index);
  }

  addNUGeraetEntry(): void {
    this.getNUGeraete().push(this.createOptionalItemGroup('geraete', {}));
  }

  removeNUGeraetEntry(index: number): void {
    this.getNUGeraete().removeAt(index);
  }

  addNUMaterialEntry(): void {
    this.getNUMaterial().push(this.createNUMaterialGroup({}));
  }

  removeNUMaterialEntry(index: number): void {
    this.getNUMaterial().removeAt(index);
  }

  addNUWeisungEntry(): void {
    this.getNUWeisungen().push(this.createNUWeisungGroup({}));
  }

  removeNUWeisungEntry(index: number): void {
    this.getNUWeisungen().removeAt(index);
  }

  isTagesberichtNUSectionSelected(sectionId: string): boolean {
    return (this.form.get('selectedSectionIds')?.value || []).includes(sectionId);
  }

  isNUSystemTemplate(template: BautagebuchTemplateDTO): boolean {
    return template?.isSystem === true;
  }

  isNUSelectedTemplateSystem(): boolean {
    if (!this.tagesberichtNUTemplateId) { return false; }
    const template = this.tagesberichtNUTemplates.find(t => t.id === this.tagesberichtNUTemplateId);
    return this.isNUSystemTemplate(template);
  }

  addTagesberichtNUSection(sectionId: string): void {
    const current = [...(this.form.get('selectedSectionIds')?.value || [])];
    if (!current.includes(sectionId)) {
      current.push(sectionId);
      this.form.get('selectedSectionIds')?.setValue(current);
    }

    // Automatically add first item for multi-entry sections
    const targetArray = this.form.get(sectionId);
    if (targetArray instanceof FormArray && targetArray.length === 0) {
      targetArray.push(this.createNUOptionalItemGroup(sectionId, {}));
    }

    this.nuActiveOptionalSectionId = sectionId;
    this.refreshTagesberichtNUViewState();
  }

  removeTagesberichtNUSection(sectionId: string): void {
    const current = (this.form.get('selectedSectionIds')?.value || []).filter((id: string) => id !== sectionId);
    this.form.get('selectedSectionIds')?.setValue(current);
    this.refreshTagesberichtNUViewState();
  }

  private refreshTagesberichtNUViewState(): void {
    this.refreshNUBehinderungControls();
    this.refreshNUOptionalSections();
    this.refreshNUCatalog();
    this.refreshWeatherSummary();
  }

  private refreshNUBehinderungControls(): void {
    this.nuBehinderungControls = (this.getNUBehinderungen()?.controls || []) as FormGroup[];
  }

  private refreshNUOptionalSections(): void {
    if (!this.tagesberichtNUSections) {
      this.tagesberichtNUActiveOptionalSections = [];
      return;
    }
    const selected = new Set((this.form.get('selectedSectionIds')?.value || []) as string[]);
    const coreIds = new Set(['project', 'day', 'weather', 'nuFirma', 'ausgefuehrteArbeiten']);
    this.tagesberichtNUActiveOptionalSections = this.tagesberichtNUSections.groups
      .reduce((sections, group) => sections.concat(group.sections), [] as BautagebuchSectionDTO[])
      .filter(section => !section.enabledByDefault || !coreIds.has(section.id))
      .filter(section => !section.internal)
      .filter(section => selected.has(section.id))
      .map(section => {
        const keys = this.getOptionalSectionKeys(section.id);
        const fields = keys.map(key => ({
          key,
          label: this.getOptionalFieldLabel(section.id, key),
          inputType: this.getOptionalInputType(section.id, key),
          options: this.getOptionalFieldOptions(section.id, key)
        }));

        return {
          id: section.id,
          title: section.title,
          previewFields: section.previewFields,
          entryCount: this.getNUSectionEntryCount(section.id),
          items: (this.form.get(section.id) as FormArray)?.value || [],
          fields
        };
      });

    if (!this.nuActiveOptionalSectionId || !this.tagesberichtNUActiveOptionalSections.some(s => s.id === this.nuActiveOptionalSectionId)) {
      this.nuActiveOptionalSectionId = this.tagesberichtNUActiveOptionalSections[0]?.id || null;
    }
  }

  private refreshNUCatalog(): void {
    if (!this.tagesberichtNUSections) {
      this.tagesberichtNUOptionalCatalogGroups = [];
      return;
    }
    const selected = new Set((this.form.get('selectedSectionIds')?.value || []) as string[]);
    this.tagesberichtNUOptionalCatalogGroups = this.tagesberichtNUSections.groups
      .filter(group => group.id !== 'core' && group.id !== 'intern')
      .map(group => ({
        ...group,
        sections: group.sections.filter(section => !selected.has(section.id) && section.id !== 'ausgefuehrteArbeiten')
      }))
      .filter(group => group.sections.length > 0);
  }

  getNUSectionFormArray(sectionId: string): FormArray {
    return this.form.get(sectionId) as FormArray;
  }

  getNUSectionEntryCount(sectionId: string): number {
    if (sectionId === 'behinderungen') { return this.nuBehinderungControls.length; }
    const arr = this.form.get(sectionId) as FormArray;
    return arr ? arr.length : 0;
  }

  addNUSectionItem(sectionId: string): void {
    const arr = this.form.get(sectionId) as FormArray;
    if (arr) { arr.push(this.createOptionalItemGroup(sectionId, {})); }
  }

  removeNUSectionItem(sectionId: string, index: number): void {
    const arr = this.form.get(sectionId) as FormArray;
    if (arr) { arr.removeAt(index); }
  }

  private loadTagesberichtNUTemplates(): void {
    this.http.get<BautagebuchTemplateDTO[]>(
      this.getTemplatesUrl(BautagebuchLegacyFormComponent.TAGESBERICHT_NU_MESSAGE_TYPE),
      { params: { messageType: BautagebuchLegacyFormComponent.TAGESBERICHT_NU_MESSAGE_TYPE } }
    ).subscribe({
      next: (templates) => { this.tagesberichtNUTemplates = templates; },
      error: () => { this.tagesberichtNUTemplates = []; }
    });
  }

  applyTagesberichtNUTemplate(templateId: string): void {
    const template = this.tagesberichtNUTemplates.find(t => t.id === templateId);
    if (!template) { return; }
    
    // Preserve current date
    const currentDate = this.form.get('day.datum')?.value;
    
    const mergedValues = this.deepMerge(this.tagesberichtNUSections?.defaultValues || {}, template.data || {});
    
    if (currentDate) {
      if (!mergedValues.day) mergedValues.day = {};
      mergedValues.day.datum = currentDate;
    }
    
    this.tagesberichtNUTemplateId = template.id;
    this.initializeTagesberichtNUForm(mergedValues);
  }

  saveTagesberichtNUTemplate(updateExisting: boolean): void {
    const existing = this.tagesberichtNUTemplates.find(t => t.id === this.tagesberichtNUTemplateId && !(t as any).isSystem);
    const defaultName = updateExisting && existing ? existing.name : 'Tagesbericht NU Vorlage';
    const name = window.prompt('Vorlagenname', defaultName);
    if (!name) { return; }

    const payload: any = {
      id: updateExisting && existing ? existing.id : undefined,
      messageType: BautagebuchLegacyFormComponent.TAGESBERICHT_NU_MESSAGE_TYPE,
      name,
      data: this.form.getRawValue()
    };

    const request$ = updateExisting && existing
      ? this.http.put<BautagebuchTemplateDTO>(this.getTemplatesUrl(BautagebuchLegacyFormComponent.TAGESBERICHT_NU_MESSAGE_TYPE), payload)
      : this.http.post<BautagebuchTemplateDTO>(this.getTemplatesUrl(BautagebuchLegacyFormComponent.TAGESBERICHT_NU_MESSAGE_TYPE), payload);

    request$.subscribe({
      next: (saved) => {
        this.tagesberichtNUTemplateId = saved.id;
        this.loadTagesberichtNUTemplates();
        this.snackBar.open('Vorlage gespeichert', 'Schließen', { duration: 2000 });
      },
      error: () => {
        this.snackBar.open('Vorlage konnte nicht gespeichert werden', 'Schließen', { duration: 4000, panelClass: ['error-snackbar'] });
      }
    });
  }

  deleteTagesberichtNUTemplate(): void {
    if (!this.tagesberichtNUTemplateId) { return; }
    const template = this.tagesberichtNUTemplates.find(t => t.id === this.tagesberichtNUTemplateId);
    if (template && this.isNUSystemTemplate(template)) { return; }
    if (!window.confirm('Vorlage wirklich löschen?')) { return; }
    this.http.delete(`${this.getTemplatesUrl(BautagebuchLegacyFormComponent.TAGESBERICHT_NU_MESSAGE_TYPE)}/${this.tagesberichtNUTemplateId}`)
      .subscribe({
        next: () => {
          this.tagesberichtNUTemplateId = null;
          this.loadTagesberichtNUTemplates();
          this.snackBar.open('Vorlage gelöscht', 'Schließen', { duration: 2000 });
        },
        error: () => {
          this.snackBar.open('Vorlage konnte nicht gelöscht werden', 'Schließen', { duration: 4000, panelClass: ['error-snackbar'] });
        }
      });
  }

  previewTagesberichtNUXml(): void {
    this.loading = true;
    const submission: SimplifiedFormSubmissionDTO = {
      messageType: BautagebuchLegacyFormComponent.TAGESBERICHT_NU_MESSAGE_TYPE,
      sectionPath: BautagebuchLegacyFormComponent.TAGESBERICHT_NU_SECTION_PATH,
      fieldValues: this.form.getRawValue(),
      caseId: this.caseId
    };
    this.http.post(this.getGenerateXmlUrl(BautagebuchLegacyFormComponent.TAGESBERICHT_NU_MESSAGE_TYPE), submission, { responseType: 'text' })
      .subscribe({
        next: (xml) => {
          this.loading = false;
          this.dialog.open(XmlPreviewDialogComponent, { width: '70vw', data: { xml } });
        },
        error: (error) => {
          this.loading = false;
          this.snackBar.open(`Fehler bei der XML-Vorschau: ${error.message || 'Unbekannter Fehler'}`, 'Schließen', { duration: 5000, panelClass: ['error-snackbar'] });
        }
      });
  }

  private async saveTagesberichtNUDraft(closeAfterSave: boolean, fieldValuesOverride?: any, status?: BautagebuchStatus): Promise<boolean> {
    if (this.datumPickerControl.invalid) {
      this.datumPickerControl.markAsTouched();
      this.snackBar.open('Bitte ein gültiges Datum eingeben (TT.MM.JJJJ).', 'Schließen', { duration: 4000, panelClass: ['error-snackbar'] });
      return false;
    }

    this.nuDraftSaveInProgress = true;
    const draftRequest: any = {
      messageType: BautagebuchLegacyFormComponent.TAGESBERICHT_NU_MESSAGE_TYPE,
      sectionPath: BautagebuchLegacyFormComponent.TAGESBERICHT_NU_SECTION_PATH,
      fieldValues: fieldValuesOverride || this.form.getRawValue(),
      caseId: this.caseId,
      destinationId: this.destinationId,
      serviceType: this.serviceType,
      serviceName: this.serviceName,
      draftId: this.draftId,
      status: status
    };

    return new Promise((resolve) => {
      this.http.post<any>(
        this.getSaveDraftUrl(BautagebuchLegacyFormComponent.TAGESBERICHT_NU_MESSAGE_TYPE),
        draftRequest,
        this.getDraftRequestOptions()
      )
        .subscribe({
          next: (response) => {
            this.nuDraftSaveInProgress = false;
            this.draftId = response.draftId;
            this.initialFormValue = this.form.getRawValue();
            this.lastSavedResponse = response;
            this.snackBar.open(status === BautagebuchStatus.SUBMITTED ? 'Tagesbericht finalisiert' : 'Tagesbericht-Entwurf gespeichert', 'Schließen', { duration: 2000 });
            this.loadCurrentNUDataspaceAsset();
            if (closeAfterSave) { this.dialogRef.close(response); }
            resolve(true);
          },
          error: (error) => {
            this.nuDraftSaveInProgress = false;
            this.snackBar.open(`Fehler beim Speichern: ${error.message}`, 'Schließen', { duration: 5000, panelClass: ['error-snackbar'] });
            resolve(false);
          }
        });
    });
  }

  async finaliseTagesberichtNU(): Promise<void> {
    const finalisedFieldValues = this.withNuInternalStatus(
      this.form.getRawValue(),
      BautagebuchLegacyFormComponent.NU_FINALISED_STATUS
    );
    const saved = await this.saveTagesberichtNUDraft(false, finalisedFieldValues, BautagebuchStatus.SUBMITTED);
    if (!saved || !this.draftId) { return; }
    // No second snackbar needed as saveTagesberichtNUDraft now handles it
    this.dialogRef.close({ draftId: this.draftId, finalised: true });
  }

  getNUWorkerControls(): FormGroup[] {
    return (this.getNUArbeitskraefte()?.controls || []) as FormGroup[];
  }

  showNuPreviewSection(sectionId: string): boolean {
    if (!this.isReadOnlyPreview || !this.previewVisibleNuSectionIds) {
      return true;
    }
    return this.previewVisibleNuSectionIds.includes(sectionId);
  }

  private setupBautagebuchMode(): void {
    this.loading = true;
    this.error = null;

    this.http.get<BautagebuchSectionsResponseDTO>(
      this.getSectionsUrl()
    ).subscribe({
      next: (sections) => {
        this.bautagebuchSections = sections;
        const mergedValues = this.deepMerge(sections.defaultValues || {}, this.existingFieldValues || {});

        if (!mergedValues.project) { mergedValues.project = {}; }
        if (!mergedValues.project.bezeichnungDerBaumassnahme) {
          mergedValues.project.bezeichnungDerBaumassnahme = this.serviceName || 'Bautagebuch Bautagebuch';
        }

        if (!mergedValues.day) { mergedValues.day = {}; }
        if (!mergedValues.day.datum) {
          mergedValues.day.datum = new Date().toISOString().slice(0, 10);
        }

        this.initializeBautagebuchForm(mergedValues);
        this.dataspaceCatalogProjectKey = mergedValues.project?.bezeichnungDerBaumassnahme || '';
        this.dataspaceCatalogReportDate = mergedValues.day?.datum || '';
        this.initialFormValue = this.form.getRawValue();
        this.loading = false;
        this.loadBautagebuchTemplates();
      },
      error: (error) => {
        this.loading = false;
        this.error = `Fehler beim Laden des Bautagebuchs: ${error.message || 'Unbekannter Fehler'}`;
      }
    });
  }

  private initializeBautagebuchForm(values: any): void {
    this.form = this.fb.group({
      selectedSectionIds: [values.selectedSectionIds || ['project', 'day', 'weather', 'companyReports']],
      project: this.fb.group({
        bezeichnungDerBaumassnahme: [values.project?.bezeichnungDerBaumassnahme || ''],
        auftraggeber: this.fb.group({
          name: [values.project?.auftraggeber?.name || ''],
          adresse: this.fb.group({
            strasse: [values.project?.auftraggeber?.adresse?.strasse || ''],
            hausnummer: [values.project?.auftraggeber?.adresse?.hausnummer || ''],
            plz: [values.project?.auftraggeber?.adresse?.plz || ''],
            ort: [values.project?.auftraggeber?.adresse?.ort || ''],
            land: [values.project?.auftraggeber?.adresse?.land || ''],
          }),
          kontaktperson: this.fb.group({
            vorname: [values.project?.auftraggeber?.kontaktperson?.vorname || ''],
            nachname: [values.project?.auftraggeber?.kontaktperson?.nachname || ''],
            eMail: [values.project?.auftraggeber?.kontaktperson?.eMail || ''],
          })
        }),
        bearbeiter: this.fb.group({
          vorname: [values.project?.bearbeiter?.vorname || ''],
          nachname: [values.project?.bearbeiter?.nachname || ''],
          eMail: [values.project?.bearbeiter?.eMail || '', Validators.email]
        })
      }),
      day: this.fb.group({
        datum: [values.day?.datum || '']
      }),
      weather: this.fb.group({
        tageslage: [values.weather?.tageslage || 'SONNIG'],
        temperaturMinC: [values.weather?.temperaturMinC ?? 0],
        temperaturMaxC: [values.weather?.temperaturMaxC ?? 14],
        niederschlagProzent: [values.weather?.niederschlagProzent ?? ''],
        windGeschwindigkeitKmh: [values.weather?.windGeschwindigkeitKmh ?? ''],
        windRichtung: [values.weather?.windRichtung || ''],
        bemerkung: [values.weather?.bemerkung || ''],
        messungen: this.fb.array((values.weather?.messungen || []).map((entry: any) => this.createWeatherMeasurementGroup(entry)))
      }),
      companyReports: this.fb.array((values.companyReports || []).map((entry: any) => this.createCompanyReportGroup(entry))),
      personal: this.fb.array((values.personal || []).map((entry: any) => this.createOptionalItemGroup('personal', entry))),
      nachunternehmer: this.fb.array((values.nachunternehmer || []).map((entry: any) => this.createOptionalItemGroup('nachunternehmer', entry))),
      geraete: this.fb.array((values.geraete || []).map((entry: any) => this.createOptionalItemGroup('geraete', entry))),
      material: this.fb.array((values.material || []).map((entry: any) => this.createOptionalItemGroup('material', entry))),
      progress: this.fb.array((values.progress || []).map((entry: any) => this.createOptionalItemGroup('progress', entry))),
      pruefungen: this.fb.array((values.pruefungen || []).map((entry: any) => this.createOptionalItemGroup('pruefungen', entry))),
      verdeckteLeistungen: this.fb.array((values.verdeckteLeistungen || []).map((entry: any) => this.createOptionalItemGroup('verdeckteLeistungen', entry))),
      vorkommnisse: this.fb.array((values.vorkommnisse || []).map((entry: any) => this.createOptionalItemGroup('vorkommnisse', entry))),
      anordnungen: this.fb.array((values.anordnungen || []).map((entry: any) => this.createOptionalItemGroup('anordnungen', entry))),
      abweichungen: this.fb.array((values.abweichungen || []).map((entry: any) => this.createOptionalItemGroup('abweichungen', entry))),
      wasserstaende: this.fb.array((values.wasserstaende || []).map((entry: any) => this.createOptionalItemGroup('wasserstaende', entry))),
      aufmasse: this.fb.array((values.aufmasse || []).map((entry: any) => this.createOptionalItemGroup('aufmasse', entry))),
      abnahmen: this.fb.array((values.abnahmen || []).map((entry: any) => this.createOptionalItemGroup('abnahmen', entry))),
      dokumentation: this.fb.group({
        notizen: this.fb.array((values.dokumentation?.notizen || []).map((entry: any) => this.fb.control(entry)))
      }),
      freitext: [values.freitext || ''],
      importedSourceReports: [values.importedSourceReports || []]
    });

    this.bautagebuchImportedSources = this.normalizeImportedSources(values.importedSourceReports);
    this.form.get('importedSourceReports')?.setValue(this.bautagebuchImportedSources, { emitEvent: false });

    if (this.weatherMeasurements.length === 0) {
      this.weatherMeasurements.push(this.createWeatherMeasurementGroup({ uhrzeit: '08:00:00', temperaturC: 2, wetterlage: 'SONNIG', notiz: 'Kalter Start; Vorsicht bei wasserbasierten Materialien.' }));
      this.weatherMeasurements.push(this.createWeatherMeasurementGroup({ uhrzeit: '14:00:00', temperaturC: 13, wetterlage: 'SONNIG', notiz: 'Ideale Bedingungen für die meisten Außenarbeiten.' }));
    }

    if (this.companyReports.length === 0) {
      this.companyReports.push(this.createCompanyReportGroup({}));
    }
    if (!this.companyReports.controls.some(control => !(control as FormGroup).get('sourceAssetId')?.value)) {
      this.companyReports.push(this.createCompanyReportGroup({}));
    }

    this.datumPickerControl.setValue(this.parseBautagebuchDate(values.day?.datum), { emitEvent: false });
    this.bindBautagebuchViewState();
    this.refreshBautagebuchViewState();
    this.applyReadOnlyPreviewState();
  }

  get weatherMeasurements(): FormArray { return this.form.get('weather.messungen') as FormArray; }
  get companyReports(): FormArray { return this.form.get('companyReports') as FormArray; }
  getDocumentationNotes(): FormArray { return this.form.get('dokumentation.notizen') as FormArray; }

  get bautagebuchSchemaPageCount(): number {
    return this.bautagebuchSchemaTree?.children?.filter(child => !child.deleted).length || 0;
  }

  get bautagebuchSchemaIsLastPage(): boolean {
    return this.bautagebuchSchemaPage >= this.bautagebuchSchemaPageCount - 1;
  }

  getSchemaSectionFields(sectionId: string): FieldDefinitionDTO[] {
    return this.bautagebuchSchemaSections.find(section => section.id === sectionId)?.fields || [];
  }

  getSchemaFieldValue(sectionId: string, fieldName: string): any {
    return this.bautagebuchSchemaFieldValues[sectionId]?.[fieldName] ?? '';
  }

  setSchemaFieldValue(sectionId: string, fieldName: string, value: any): void {
    if (!this.bautagebuchSchemaFieldValues[sectionId]) {
      this.bautagebuchSchemaFieldValues[sectionId] = {};
    }
    this.bautagebuchSchemaFieldValues[sectionId][fieldName] = value;
  }

  private createWeatherMeasurementGroup(entry: any): FormGroup {
    return this.fb.group({
      uhrzeit: [entry?.uhrzeit || ''],
      temperaturC: [entry?.temperaturC ?? ''],
      wetterlage: [entry?.wetterlage || 'SONNIG'],
      notiz: [entry?.notiz || '']
    });
  }

  private createCompanyReportGroup(entry: any): FormGroup {
    return this.fb.group({
      sourceAssetId: [entry?.sourceAssetId || ''],
      firma: this.fb.group({
        name: [entry?.firma?.name || '']
      }),
      arbeitszeit: this.fb.group({
        beginn: [entry?.arbeitszeit?.beginn || '07:00:00'],
        ende: [entry?.arbeitszeit?.ende || '17:00:00']
      }),
      schlechtesWetter: this.fb.group({
        beginn: [entry?.schlechtesWetter?.beginn || ''],
        ende: [entry?.schlechtesWetter?.ende || '']
      }),
      arbeitskraefte: this.fb.array((entry?.arbeitskraefte || []).map((worker: any) => this.createWorkerGroup(worker))),
      ausgefuehrteArbeiten: [entry?.ausgefuehrteArbeiten || ''],
      arbeitsfortschritt: [entry?.arbeitsfortschritt || ''],
      sonstiges: [entry?.sonstiges || '']
    });
  }

  private createWorkerGroup(entry: any): FormGroup {
    return this.fb.group({
      qualifikation: [entry?.qualifikation || 'FACHARBEITER'],
      anzahl: [entry?.anzahl ?? 1],
      lohngruppe: [entry?.lohngruppe || '']
    });
  }

  private createOptionalItemGroup(sectionId: string, entry: any): FormGroup {
    const defaults = this.getOptionalSectionShape(sectionId);
    const merged = { ...defaults, ...(entry || {}) };
    return this.fb.group(
      Object.keys(merged).reduce((acc, key) => {
        acc[key] = [merged[key]];
        return acc;
      }, {} as {[key: string]: any})
    );
  }

  private getOptionalSectionShape(sectionId: string): {[key: string]: any} {
    const shapes: {[key: string]: {[key: string]: any}} = {
      personal: { personOderGruppe: '', qualifikation: '', anzahl: '', lohngruppe: '', taetigkeit: '', notiz: '' },
      nachunternehmer: { gewerkOderTaetigkeit: '', personalzahl: '', aufwandStunden: '', bemerkung: '' },
      geraete: { bezeichnung: '', typ: '', status: '', notiz: '' },
      material: { materialOderBauteil: '', menge: '', mengeneinheit: '', notiz: '' },
      progress: { beschreibung: '', status: '', menge: '', bemerkung: '' },
      pruefungen: { pruefart: '', beschreibung: '', ergebnis: '' },
      verdeckteLeistungen: { beschreibung: '', ort: '', bemerkung: '' },
      vorkommnisse: { typ: '', beschreibung: '', ursache: '' },
      anordnungen: { typ: '', inhalt: '', rechtsgrundlage: '' },
      abweichungen: { typ: '', beschreibung: '', begruendung: '' },
      wasserstaende: { typ: '', messwert: '', einheit: '', bemerkung: '' },
      aufmasse: { beschreibung: '', datum: '', referenzDokument: '' },
      abnahmen: { typ: '', datum: '', ergebnis: '' }
    };

    // Special handling for requested sub-sections mapping to ensure they have fields
    const aliasMap: {[key: string]: string} = {
      'qualitaetPruefungen': 'pruefungen',
      'verdeckteLeistungen': 'verdeckteLeistungen',
      'besondereVorkommnisse': 'vorkommnisse'
    };
    
    const targetId = aliasMap[sectionId] || sectionId;
    return shapes[targetId] || {};
  }

  getOptionalSectionKeys(sectionId: string): string[] {
    return Object.keys(this.getOptionalSectionShape(sectionId));
  }

  getOptionalFieldLabel(sectionId: string, key: string): string {
    const aliasMap: {[key: string]: string} = {
      'qualitaetPruefungen': 'pruefungen',
      'verdeckteLeistungen': 'verdeckteLeistungen',
      'besondereVorkommnisse': 'vorkommnisse'
    };
    const targetId = aliasMap[sectionId] || sectionId;

    const labels: {[key: string]: {[key: string]: string}} = {
      personal: { personOderGruppe: 'Person / Gruppe', qualifikation: 'Qualifikation', anzahl: 'Anzahl', lohngruppe: 'Lohngruppe', taetigkeit: 'Tätigkeit', notiz: 'Notiz' },
      nachunternehmer: { gewerkOderTaetigkeit: 'Gewerk / Tätigkeit', personalzahl: 'Personalzahl', aufwandStunden: 'Aufwand Stunden', bemerkung: 'Bemerkung' },
      geraete: { bezeichnung: 'Bezeichnung', typ: 'Typ', status: 'Status', notiz: 'Notiz' },
      material: { materialOderBauteil: 'Material / Bauteil', menge: 'Menge', mengeneinheit: 'Mengeneinheit', notiz: 'Notiz' },
      progress: { beschreibung: 'Beschreibung', status: 'Status', menge: 'Menge', bemerkung: 'Bemerkung' },
      pruefungen: { pruefart: 'Prüfart', beschreibung: 'Beschreibung', ergebnis: 'Ergebnis' },
      verdeckteLeistungen: { beschreibung: 'Beschreibung', ort: 'Ort', bemerkung: 'Bemerkung' },
      vorkommnisse: { typ: 'Typ', beschreibung: 'Beschreibung', ursache: 'Ursache' },
      anordnungen: { typ: 'Typ', inhalt: 'Inhalt', rechtsgrundlage: 'Rechtsgrundlage' },
      abweichungen: { typ: 'Typ', beschreibung: 'Beschreibung', begruendung: 'Begründung' },
      wasserstaende: { typ: 'Typ', messwert: 'Messwert', einheit: 'Einheit', bemerkung: 'Bemerkung' },
      aufmasse: { beschreibung: 'Beschreibung', datum: 'Datum', referenzDokument: 'Referenzdokument' },
      abnahmen: { typ: 'Typ', datum: 'Datum', ergebnis: 'Ergebnis' }
    };
    return labels[targetId]?.[key] || key;
  }

  getOptionalInputType(sectionId: string, key: string): string {
    const normalized = key.toLowerCase();
    if (sectionId === 'geraete' && normalized === 'status') { return 'select'; }
    if (normalized.includes('datum')) { return 'date'; }
    if (normalized.includes('anzahl') || normalized.includes('stunden') || normalized.includes('messwert') || normalized.includes('menge') || normalized.includes('zahl')) {
      return 'number';
    }
    return 'text';
  }

  getOptionalFieldOptions(sectionId: string, key: string): BautagebuchOptionDTO[] {
    if (sectionId === 'geraete' && key === 'status') {
      return BautagebuchLegacyFormComponent.GERAETE_STATUS_OPTIONS;
    }
    return [];
  }

  getGeraeteStatusOptions(): BautagebuchOptionDTO[] {
    return BautagebuchLegacyFormComponent.GERAETE_STATUS_OPTIONS;
  }

  private loadBautagebuchTemplates(): void {
    this.http.get<BautagebuchTemplateDTO[]>(
      this.getTemplatesUrl(BautagebuchLegacyFormComponent.BAUTAGEBUCH_MESSAGE_TYPE),
      { params: { messageType: this.messageType } }
    ).subscribe({
      next: (templates) => { this.bautagebuchTemplates = templates; },
      error: () => { this.bautagebuchTemplates = []; }
    });
  }

  isBautagebuchSectionSelected(sectionId: string): boolean {
    return (this.form.get('selectedSectionIds')?.value || []).includes(sectionId);
  }

  onBautagebuchSearchChange(search: string): void {
    this.bautagebuchSearch = search || '';
    this.refreshBautagebuchCatalog();
  }

  addBautagebuchSection(sectionId: string): void {
    const current = [...(this.form.get('selectedSectionIds')?.value || [])];
    if (!current.includes(sectionId)) {
      current.push(sectionId);
      this.form.get('selectedSectionIds')?.setValue(current);
    }

    const targetArray = this.form.get(sectionId);
    if (targetArray instanceof FormArray && targetArray.length === 0) {
      targetArray.push(this.createOptionalItemGroup(sectionId, {}));
    }
    if (sectionId === 'dokumentation' && this.getDocumentationNotes().length === 0) {
      this.addDocumentationNote();
    }
    this.activeOptionalSectionId = sectionId;
    this.refreshBautagebuchViewState();
  }

  removeBautagebuchSection(sectionId: string): void {
    const current = (this.form.get('selectedSectionIds')?.value || []).filter((id: string) => id !== sectionId);
    this.form.get('selectedSectionIds')?.setValue(current);
    if (this.activeOptionalSectionId === sectionId) {
      this.activeOptionalSectionId = this.bautagebuchActiveOptionalSections[0]?.id || null;
    }
    this.refreshBautagebuchViewState();
  }

  openOptionalSection(sectionId: string): void { this.activeOptionalSectionId = sectionId; }

  addCompanyReport(): void {
    this.companyReports.push(this.createCompanyReportGroup({}));
    this.refreshCompanyReportEntries();
  }

  removeCompanyReport(index: number): void {
    this.companyReports.removeAt(index);
    this.refreshCompanyReportEntries();
  }

  getWorkers(reportIndex: number): FormArray {
    return this.companyReports.at(reportIndex).get('arbeitskraefte') as FormArray;
  }

  addWorker(reportIndex: number): void {
    const worker = this.createWorkerGroup({});
    this.getWorkers(reportIndex).push(worker);
    this.refreshCompanyReportEntries();
  }

  removeWorker(reportIndex: number, workerIndex: number): void {
    this.getWorkers(reportIndex).removeAt(workerIndex);
    this.refreshCompanyReportEntries();
  }

  onWorkerOptionChange(reportIndex: number, workerIndex: number, fieldName: 'qualifikation' | 'lohngruppe', selectedValue: string): void {
    const worker = this.getWorkerControl(reportIndex, workerIndex);
    const control = worker.get(fieldName);

    if (selectedValue === this.customWorkerOptionValue) {
      this.setWorkerCustomField(worker, fieldName, true);
      control?.setValue('');
      return;
    }

    this.setWorkerCustomField(worker, fieldName, false);
    control?.setValue(selectedValue);
  }

  isWorkerFieldCustom(reportIndex: number, workerIndex: number, fieldName: 'qualifikation' | 'lohngruppe'): boolean {
    const worker = this.getWorkerControl(reportIndex, workerIndex);
    const currentValue = worker.get(fieldName)?.value;
    const optionValues = (fieldName === 'qualifikation' ? this.workerQualificationOptions : this.workerLohngruppeOptions)
      .map(option => option.value);
    const customState = this.workerCustomFields.get(worker)?.[fieldName] || false;

    return customState || (!!currentValue && !optionValues.includes(currentValue));
  }

  onBautagebuchDateChange(value: Date | null): void {
    if (!value) {
      this.bautagebuchDateControl.setValue('');
      if (this.isBautagebuchMode) { this.dataspaceCatalogReportDate = ''; }
      return;
    }

    const normalized = new Date(value);
    normalized.setMinutes(normalized.getMinutes() - normalized.getTimezoneOffset());
    const isoDate = normalized.toISOString().slice(0, 10);
    this.bautagebuchDateControl.setValue(isoDate);
    if (this.isBautagebuchMode) { this.dataspaceCatalogReportDate = isoDate; }
  }

  addOptionalItem(sectionId: string): void {
    const targetArray = this.form.get(sectionId) as FormArray;
    targetArray.push(this.createOptionalItemGroup(sectionId, {}));
    this.activeOptionalSectionId = sectionId;
    this.refreshActiveOptionalSections();
  }

  removeOptionalItem(sectionId: string, index: number): void {
    const targetArray = this.form.get(sectionId) as FormArray;
    targetArray.removeAt(index);
    this.refreshActiveOptionalSections();
  }

  addDocumentationNote(): void {
    this.getDocumentationNotes().push(this.fb.control(''));
    this.refreshActiveOptionalSections();
  }

  removeDocumentationNote(index: number): void {
    this.getDocumentationNotes().removeAt(index);
    this.refreshActiveOptionalSections();
  }

  applyBautagebuchTemplate(templateId: string): void {
    const template = this.bautagebuchTemplates.find(entry => entry.id === templateId);
    if (!template) { return; }

    // Preserve current date
    const currentDate = this.form.get('day.datum')?.value;

    const mergedValues = this.deepMerge(this.bautagebuchSections?.defaultValues || {}, template.data || {});

    if (currentDate) {
      if (!mergedValues.day) mergedValues.day = {};
      mergedValues.day.datum = currentDate;
    }

    this.bautagebuchTemplateId = template.id;
    this.initializeBautagebuchForm(mergedValues);
  }

  setBautagebuchViewMode(mode: 'quick' | 'schema'): void {
    this.bautagebuchViewMode = mode;
    if (mode === 'schema' && !this.bautagebuchSchemaSections.length && !this.bautagebuchSchemaLoading) {
      this.loadBautagebuchSchemaView();
    }
  }

  nextBautagebuchSchemaPage(): void { if (!this.bautagebuchSchemaIsLastPage) { this.bautagebuchSchemaPage++; } }
  prevBautagebuchSchemaPage(): void { if (this.bautagebuchSchemaPage > 0) { this.bautagebuchSchemaPage--; } }

  saveBautagebuchTemplate(updateExisting: boolean): void {
    const existing = this.bautagebuchTemplates.find(entry => entry.id === this.bautagebuchTemplateId);
    const defaultName = updateExisting && existing ? existing.name : 'Bautagebuch Vorlage';
    const name = window.prompt('Vorlagenname', defaultName);
    if (!name) { return; }

    const payload: any = {
      id: updateExisting && existing ? existing.id : undefined,
      messageType: this.messageType,
      name,
      data: this.form.getRawValue()
    };

    const request$ = updateExisting && existing
      ? this.http.put<BautagebuchTemplateDTO>(this.getTemplatesUrl(BautagebuchLegacyFormComponent.BAUTAGEBUCH_MESSAGE_TYPE), payload)
      : this.http.post<BautagebuchTemplateDTO>(this.getTemplatesUrl(BautagebuchLegacyFormComponent.BAUTAGEBUCH_MESSAGE_TYPE), payload);

    request$.subscribe({
      next: (saved) => {
        this.bautagebuchTemplateId = saved.id;
        this.loadBautagebuchTemplates();
        this.snackBar.open('Vorlage gespeichert', 'Schließen', { duration: 2000 });
      },
      error: () => {
        this.snackBar.open('Vorlage konnte nicht gespeichert werden', 'Schließen', { duration: 4000, panelClass: ['error-snackbar'] });
      }
    });
  }

  deleteBautagebuchTemplate(): void {
    if (!this.bautagebuchTemplateId) { return; }
    if (!window.confirm('Vorlage wirklich löschen?')) { return; }

    this.http.delete(`${this.getTemplatesUrl(BautagebuchLegacyFormComponent.BAUTAGEBUCH_MESSAGE_TYPE)}/${this.bautagebuchTemplateId}`)
      .subscribe({
        next: () => {
          this.bautagebuchTemplateId = null;
          this.loadBautagebuchTemplates();
          this.snackBar.open('Vorlage gelöscht', 'Schließen', { duration: 2000 });
        },
        error: () => {
          this.snackBar.open('Vorlage konnte nicht gelöscht werden', 'Schließen', { duration: 4000, panelClass: ['error-snackbar'] });
        }
      });
  }

  previewBautagebuchXml(): void {
    this.loading = true;
    const submission: SimplifiedFormSubmissionDTO = {
      messageType: this.messageType,
      sectionPath: BautagebuchLegacyFormComponent.BAUTAGEBUCH_SECTION_PATH,
      fieldValues: this.form.getRawValue(),
      caseId: this.caseId
    };

    this.http.post(this.getGenerateXmlUrl(BautagebuchLegacyFormComponent.BAUTAGEBUCH_MESSAGE_TYPE), submission, {
      responseType: 'text'
    }).subscribe({
      next: (xml) => {
        this.loading = false;
        this.dialog.open(XmlPreviewDialogComponent, { width: '70vw', data: { xml } });
      },
      error: (error) => {
        this.loading = false;
        this.snackBar.open(`Fehler bei der XML-Vorschau: ${error.message || 'Unbekannter Fehler'}`, 'Schließen', { duration: 5000, panelClass: ['error-snackbar'] });
      }
    });
  }

  openFloorplanOverlay(markerId?: string): void {
    this.selectedFloorplanMarkerId = markerId || this.selectedFloorplanMarkerId || this.floorplanConfig.markers[0]?.id || '';
    this.isFloorplanOverlayOpen = true;
  }

  closeFloorplanOverlay(): void { this.isFloorplanOverlayOpen = false; }
  selectFloorplanMarker(markerId: string): void { this.selectedFloorplanMarkerId = markerId; }

  get selectedFloorplanMarker(): FloorplanMarkerConfig | null {
    return this.floorplanConfig.markers.find(marker => marker.id === this.selectedFloorplanMarkerId) || null;
  }

  get floorplanPreviewMedia(): MarkerLinkedMedia | null {
    return this.floorplanConfig.markers[0]?.media[0] || null;
  }

  getFloorplanMarkerStyle(marker: FloorplanMarkerConfig): {[key: string]: string} {
    return {
      left: `${(marker.x / this.floorplanConfig.originalWidth) * 100}%`,
      top: `${(marker.y / this.floorplanConfig.originalHeight) * 100}%`
    };
  }

  getCompanyReportDurationLabel(report: FormGroup, groupName: 'arbeitszeit' | 'schlechtesWetter'): string {
    const value = (report.get(groupName) as FormGroup | null)?.getRawValue();
    return this.formatDurationLabel(value?.beginn, value?.ende);
  }

  private formatDurationLabel(start?: string, end?: string): string {
    const minutes = this.calculateDurationMinutes(start, end);
    if (minutes === null) { return ''; }
    const hours = minutes / 60;
    return hours.toLocaleString('de-DE', { minimumFractionDigits: Number.isInteger(hours) ? 0 : 2, maximumFractionDigits: 2 });
  }

  private calculateDurationMinutes(start?: string, end?: string): number | null {
    const startMinutes = this.parseTimeToMinutes(start);
    const endMinutes = this.parseTimeToMinutes(end);
    if (startMinutes === null || endMinutes === null || endMinutes < startMinutes) { return null; }
    return endMinutes - startMinutes;
  }

  private parseTimeToMinutes(value?: string): number | null {
    if (!value) { return null; }
    const parts = value.split(':');
    if (parts.length < 2) { return null; }
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) { return null; }
    return (hours * 60) + minutes;
  }

  private parseBautagebuchDate(value?: string | Date | null): Date | null {
    if (!value) { return null; }
    if (value instanceof Date) { return Number.isNaN(value.getTime()) ? null : value; }
    const normalized = value.trim();
    const dotSeparatedMatch = normalized.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (dotSeparatedMatch) {
      const day = Number(dotSeparatedMatch[1]);
      const month = Number(dotSeparatedMatch[2]) - 1;
      const year = Number(dotSeparatedMatch[3]);
      const parsed = new Date(year, month, day);
      return parsed.getFullYear() === year && parsed.getMonth() === month && parsed.getDate() === day ? parsed : null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private loadBautagebuchSchemaView(): void {
    this.bautagebuchSchemaLoading = true;
    this.bautagebuchSchemaError = null;
    const sections = (this.bautagebuchSections?.groups || []).reduce((allSections, group) => {
      return allSections.concat(group.sections);
    }, [] as BautagebuchSectionDTO[]);

    if (!sections.length) {
      this.bautagebuchSchemaLoading = false;
      this.bautagebuchSchemaError = 'Schema-Bereiche konnten nicht geladen werden.';
      return;
    }

    const requests = sections.map(section =>
      this.http.get<FilteredMetadataDTO>(this.getMetadataUrl(), { params: { sectionPath: section.schemaPath } })
    );

    forkJoin(requests).subscribe({
      next: (responses) => {
        this.bautagebuchSchemaSections = sections.map((section, index) => ({
          ...section,
          fields: (((responses as any[])[index] as any)?.fields) || []
        }));
        this.bautagebuchSchemaFieldValues = this.bautagebuchSchemaSections.reduce((acc, section) => {
          acc[section.id] = acc[section.id] || {};
          section.fields.forEach(field => {
            if (acc[section.id][field.name] === undefined) {
              acc[section.id][field.name] = field.defaultValue ?? (field.type === 'boolean' ? false : '');
            }
          });
          return acc;
        }, this.bautagebuchSchemaFieldValues);
        this.bautagebuchSchemaLoading = false;
      },
      error: (error) => {
        this.bautagebuchSchemaLoading = false;
        this.bautagebuchSchemaError = `Schema konnte nicht geladen werden: ${error.message || 'Unbekannter Fehler'}`;
      }
    });
  }

  initializeForm(): void {
    const group: { [key: string]: any } = {};
    this.fieldDefinitions.forEach(field => {
      const validators = [];
      if (field.required) { validators.push(Validators.required); }
      if (field.name === 'email') { validators.push(Validators.email); }
      const initialValue = this.existingFieldValues?.[field.name] || '';
      group[field.name] = [initialValue, validators];
    });
    this.form = this.fb.group(group);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.markAllAsTouched();
      this.snackBar.open('Bitte füllen Sie alle Pflichtfelder korrekt aus', 'Schließen', { duration: 3000, panelClass: ['error-snackbar'] });
      return;
    }

    this.loading = true;
    const submission: SimplifiedFormSubmissionDTO = {
      messageType: this.messageType,
      sectionPath: this.sectionPath,
      fieldValues: this.form.value,
      caseId: this.caseId
    };

    this.http.post(this.getGenerateXmlUrl(), submission, { responseType: 'text' }).subscribe({
      next: (xml) => {
        this.loading = false;
        this.xmlGenerated.emit(xml);
        this.snackBar.open('XML erfolgreich generiert', 'Schließen', { duration: 2000 });
      },
      error: (error) => {
        this.loading = false;
        this.snackBar.open(`Fehler beim Generieren der XML: ${error.message || 'Unbekannter Fehler'}`, 'Schließen', { duration: 5000, panelClass: ['error-snackbar'] });
      }
    });
  }

  private markAllAsTouched(): void { Object.keys(this.form.controls).forEach(key => this.form.get(key)?.markAsTouched()); }

  async onSave(): Promise<void> {
    if (this.isTagesberichtNUMode) { await this.saveTagesberichtNUDraft(false); return; }
    if (this.isBautagebuchMode) { await this.saveBautagebuchDraft(false); return; }
    if (this.isMultiPage) { await this.saveMultiPageDraft(false); } else { await this.saveToDatabase(false); }
  }

  async onSaveAndClose(): Promise<void> {
    if (this.isTagesberichtNUMode) { await this.saveTagesberichtNUDraft(true); return; }
    if (this.isBautagebuchMode) { await this.saveBautagebuchDraft(true); return; }
    if (this.isMultiPage) { await this.saveMultiPageDraft(true); } else {
      if (this.isEditMode) {
        const confirmed = await this.showOverwriteWarning();
        if (!confirmed) return;
      }
      await this.saveToDatabase(true);
    }
  }

  async onCancel(): Promise<void> {
    if (this.hasUnsavedChanges) {
      const confirmed = await this.showUnsavedChangesWarning();
      if (!confirmed) return;
    }
    this.dialogRef.close(this.lastSavedResponse);
  }

  private async saveBautagebuchDraft(closeAfterSave: boolean): Promise<boolean> {
    if (this.datumPickerControl.invalid) {
      this.datumPickerControl.markAsTouched();
      this.snackBar.open('Bitte ein gültiges Datum eingeben (TT.MM.JJJJ).', 'Schließen', { duration: 4000, panelClass: ['error-snackbar'] });
      return false;
    }

    this.loading = true;
    const draftRequest: any = {
      messageType: this.messageType,
      sectionPath: BautagebuchLegacyFormComponent.BAUTAGEBUCH_SECTION_PATH,
      fieldValues: this.form.getRawValue(),
      caseId: this.caseId,
      destinationId: this.destinationId,
      serviceType: this.serviceType,
      serviceName: this.serviceName,
      draftId: this.draftId
    };

    return new Promise((resolve) => {
      this.http.post<any>(this.getSaveDraftUrl(), draftRequest, this.getDraftRequestOptions()).subscribe({
        next: (response) => {
          this.loading = false;
          this.draftId = response.draftId;
          this.initialFormValue = this.form.getRawValue();
          this.lastSavedResponse = response;
          this.snackBar.open('Bautagebuch-Entwurf gespeichert', 'Schließen', { duration: 2000 });
          if (closeAfterSave) { this.dialogRef.close(response); }
          resolve(true);
        },
        error: (error) => {
          this.loading = false;
          this.snackBar.open(`Fehler beim Speichern: ${error.message}`, 'Schließen', { duration: 5000, panelClass: ['error-snackbar'] });
          resolve(false);
        }
      });
    });
  }

  private async loadDataspaceOrganisationOptions(): Promise<void> {
    try {
      await this.organisationService.getAllOrgs();
      const activeOrgId = this.activeOrganisationId;
      const organisations = this.organisationService.sdOrgs || this.organisationService.collection || [];
      this.availableDataspaceConsumerOrganisations = organisations
        .filter(org => !!org.organisationId && org.organisationId !== activeOrgId)
        .sort((left, right) => (left.organisationName || left.name || '').localeCompare(right.organisationName || right.name || ''));
      this.filteredDataspaceConsumerOrganisations.next(this.availableDataspaceConsumerOrganisations.slice());
    } catch {
      this.availableDataspaceConsumerOrganisations = [];
      this.filteredDataspaceConsumerOrganisations.next([]);
    }
  }

  private getRequiredOrganisationId(): number | null {
    const organisationId = this.activeOrganisationId || this.data?.organisationId || this.organisationService.activeOrganisation?.organisationId || null;
    if (!organisationId) {
      const confirmedMemberships = this.getConfirmedMemberships();
      this.snackBar.open(confirmedMemberships.length > 1 ? 'Keine aktive Organisation ausgewählt. Bitte im Kopfbereich aktivieren.' : 'Keine aktive Organisation ausgewählt.', 'Schließen', { duration: 4000, panelClass: ['error-snackbar'] });
      return null;
    }
    return organisationId;
  }

  private firstValue<T>(observable: Observable<T>): Promise<T> { return observable.toPromise() as Promise<T>; }

  private async setupMultiPageWizard(): Promise<void> {
    this.messageTypeConfig = MESSAGE_TYPE_CONFIGS[this.messageType];
    if (this.messageType === 'wegesicherung.antrag.2002') {
      this.pages = [
        { title: 'Empfänger', sectionPath: 'nachrichtenkopf.g2g.leser', formGroupName: 'page1', isRepeatable: false },
        { title: 'Autor', sectionPath: 'nachrichtenkopf.g2g.autor', formGroupName: 'page2', isRepeatable: false },
        { title: 'Antragsteller', sectionPath: 'antragsteller', formGroupName: 'page3', isRepeatable: false },
        { title: 'Vertreter des Antragstellers', sectionPath: 'antragsteller.vertreter', formGroupName: 'page4', isRepeatable: true, minInstances: 1, maxInstances: 99 },
        { title: 'Ansprechpartner des Antragstellers', sectionPath: 'antragsteller.ansprechpartner', formGroupName: 'page5', isRepeatable: true, minInstances: 1, maxInstances: 99 },
        { title: 'Beteiligung Dritter', sectionPath: 'beteiligte', formGroupName: 'page6', isRepeatable: true, minInstances: 0, maxInstances: 99 },
        { title: 'Antragsverfahren', sectionPath: 'verfahrensmerkmale', formGroupName: 'page7', isRepeatable: false }
      ];
    } else if (this.messageType === 'wegesicherung.korrekturAntrag.2004') {
      this.pages = [
        { title: 'Empfänger', sectionPath: 'nachrichtenkopf.g2g.leser', formGroupName: 'page1', isRepeatable: false },
        { title: 'Autor', sectionPath: 'nachrichtenkopf.g2g.autor', formGroupName: 'page2', isRepeatable: false },
        { title: 'Hinweis zur Korrektur', sectionPath: 'hinweisZurKorrektur', formGroupName: 'page3', isRepeatable: false },
        { title: 'Angaben zum Antragsverfahren', sectionPath: 'verfahrensmerkmale', formGroupName: 'page4', isRepeatable: false }
      ];
    } else if (this.messageType === 'wegesicherung.nachbesserungAntrag.2006') {
      this.pages = [
        { title: 'Empfänger (vorausgefüllt aus 2005)', sectionPath: 'nachrichtenkopf.g2g.leser', formGroupName: 'page1', isRepeatable: false },
        { title: 'Absender', sectionPath: 'nachrichtenkopf.g2g.autor', formGroupName: 'page2', isRepeatable: false },
        { title: 'Allgemeine Stellungnahme', sectionPath: 'stellungnahme', formGroupName: 'page3', isRepeatable: false },
        { title: 'Stellungnahmen zu Befunden', sectionPath: 'befundliste.befund', formGroupName: 'page4', isRepeatable: true, minInstances: 0, maxInstances: 99, lockedInstances: true }
      ];
    }
    this.loadAllPagesMetadata();
  }

  private loadAllPagesMetadata(): void {
    this.loading = true;
    const requests = this.pages.map(page => this.http.get<FilteredMetadataDTO>(this.getMetadataUrl(), { params: { sectionPath: page.sectionPath } }));
    forkJoin(requests).subscribe({
      next: (metadataArray) => {
        metadataArray.forEach((metadata, index) => this.allFieldDefinitions.set(this.pages[index].formGroupName, metadata.fields));
        this.allFieldDefinitions.forEach(fields => fields.forEach(field => { if (field.codeListUrn?.includes('registergerichte')) { this.setupRegistergerichtFilter(field); } }));
        this.initializeMultiPageForm();
        this.loading = false;
      },
      error: (error) => {
        this.error = `Fehler beim Laden der Formulardaten: ${error.message || 'Unbekannter Fehler'}`;
        this.snackBar.open(this.error, 'Schließen', { duration: 5000, panelClass: ['error-snackbar'] });
        this.loading = false;
      }
    });
  }

  private initializeMultiPageForm(): void {
    const pageGroups: { [key: string]: any } = {};
    this.pages.forEach(page => {
      const fields = this.allFieldDefinitions.get(page.formGroupName) || [];
      if (page.isRepeatable) {
        const formArray = this.fb.array<FormGroup>([]);
        let itemCount = page.minInstances || 1;
        if (this.existingFieldValues && this.existingFieldValues[page.formGroupName]) {
          const existingData = this.existingFieldValues[page.formGroupName];
          if (Array.isArray(existingData) && existingData.length > 0) { itemCount = existingData.length; }
        }
        for (let i = 0; i < itemCount; i++) {
          const fg = this.createFormGroupForFields(fields, page.formGroupName, i);
          if (page.readOnly) { fg.disable(); }
          formArray.push(fg);
        }
        pageGroups[page.formGroupName] = formArray;
      } else {
        const formGroup = this.createFormGroupForFields(fields, page.formGroupName);
        if (page.readOnly) { formGroup.disable(); }
        pageGroups[page.formGroupName] = formGroup;
      }
    });
    this.form = this.fb.group(pageGroups);
    this.initialFormValue = this.form.value;
  }

  private getInitialValueForField(pageKey: string, fieldName: string, itemIndex?: number): any {
    if (this.existingFieldValues && this.existingFieldValues[pageKey]) {
      const data = this.existingFieldValues[pageKey];
      if (Array.isArray(data)) { return (itemIndex !== undefined && itemIndex < data.length) ? data[itemIndex][fieldName] || '' : ''; }
      return data[fieldName] || '';
    }
    return '';
  }

  private createFormGroupForFields(fields: FieldDefinitionDTO[], pageKey?: string, itemIndex?: number): FormGroup {
    const group: { [key: string]: any } = {};
    fields.forEach(field => {
      const validators = [];
      if (field.required) validators.push(Validators.required);
      if (field.name.includes('email')) validators.push(Validators.email);
      let initialValue = field.defaultValue !== undefined ? field.defaultValue : '';
      if (pageKey !== undefined) {
        const existingValue = this.getInitialValueForField(pageKey, field.name, itemIndex);
        if (existingValue) initialValue = existingValue;
      }
      group[field.name] = [initialValue, validators];
    });
    const formGroup = this.fb.group(group);
    fields.forEach(field => {
      if (field.readOnly) { const control = formGroup.get(field.name); if (control) { control.disable(); } }
      if (field.isConditional && field.conditionalField) {
        const conditionalControl = (formGroup as any).controls?.[field.conditionalField];
        if (conditionalControl) { conditionalControl.valueChanges.subscribe(() => {}); }
      }
    });
    return formGroup;
  }

  get currentPageConfig(): PageConfig { return this.pages[this.currentPageIndex]; }
  get currentFormControl(): any { return this.form.get(this.currentPageConfig.formGroupName); }
  getCurrentFormArray(): FormArray | null { const control = this.currentFormControl; return control instanceof FormArray ? control : null; }

  addInstance(): void {
    const formArray = this.getCurrentFormArray(); if (!formArray) return;
    const page = this.currentPageConfig;
    if (page.maxInstances && formArray.length >= page.maxInstances) { this.snackBar.open('Maximale Anzahl erreicht', 'Schließen', { duration: 3000 }); return; }
    const fields = this.allFieldDefinitions.get(page.formGroupName) || [];
    formArray.push(this.createFormGroupForFields(fields));
  }

  removeInstance(index: number): void {
    const formArray = this.getCurrentFormArray(); if (!formArray) return;
    const page = this.currentPageConfig;
    if (page.minInstances && formArray.length <= page.minInstances) { this.snackBar.open('Mindestanzahl erreicht', 'Schließen', { duration: 3000 }); return; }
    formArray.removeAt(index);
  }

  isFieldVisible(field: FieldDefinitionDTO, instanceFormGroup?: FormGroup): boolean {
    if (!field.isConditional) return true;
    const formGroup = instanceFormGroup || this.currentFormControl;
    if (!formGroup || !field.conditionalField) return false;
    let conditionalControl = (formGroup as any).controls?.[field.conditionalField];
    if (!conditionalControl) { conditionalControl = formGroup.get(field.conditionalField); }
    if (!conditionalControl) return false;
    return conditionalControl.value === field.conditionalValue;
  }

  isPageValid(pageIndex: number): boolean {
    const page = this.pages[pageIndex];
    const formControl = this.form.get(page.formGroupName);
    if (!formControl) return false;
    if (formControl instanceof FormArray) { return formControl.valid && formControl.length >= (page.minInstances || 0); }
    return formControl.valid;
  }

  navigateToPage(pageIndex: number): void { if (pageIndex >= 0 && pageIndex < this.pages.length) { this.currentPageIndex = pageIndex; this.scrollDialogContentToTop(); } }

  private scrollDialogContentToTop(): void {
    setTimeout(() => {
      const activeContent = this.scrollableContents?.toArray().map((entry) => entry.nativeElement).find((element) => element.offsetParent !== null);
      if (activeContent) { activeContent.scrollTo({ top: 0, behavior: 'auto' }); }
    });
  }

  private async saveMultiPageDraft(closeAfterSave: boolean): Promise<void> {
    if (closeAfterSave && this.isEditMode) { const confirmed = await this.showOverwriteWarning(); if (!confirmed) return; }
    this.loading = true;
    const pageFieldValues: { [key: string]: any } = {};
    this.pages.forEach(page => {
      const control = this.form.get(page.formGroupName);
      pageFieldValues[page.formGroupName] = (control instanceof FormArray || control instanceof FormGroup) ? control.getRawValue() : control?.value;
    });

    const draftRequest: any = { messageType: this.messageType, sectionPath: 'multi-page', fieldValues: pageFieldValues, caseId: this.caseId, destinationId: this.destinationId, serviceType: this.serviceType, serviceName: this.serviceName, draftId: this.draftId };
    if (this.inReplyToMessageId && this.inReplyToMessageType) { draftRequest.inReplyToMessageId = this.inReplyToMessageId; draftRequest.inReplyToMessageType = this.inReplyToMessageType; }

    this.http.post<any>(this.getSaveDraftUrl(), draftRequest, this.getDraftRequestOptions()).subscribe({
      next: (response) => {
        this.loading = false; this.draftId = response.draftId; this.initialFormValue = this.form.value; this.lastSavedResponse = response;
        this.snackBar.open('Entwurf erfolgreich gespeichert', 'Schließen', { duration: 2000 });
        if (closeAfterSave) { this.dialogRef.close(response); }
      },
      error: (error) => { this.loading = false; this.snackBar.open(`Fehler beim Speichern: ${error.message}`, 'Schließen', { duration: 5000, panelClass: ['error-snackbar'] }); }
    });
  }

  trackByCode(index: number, item: CodeDTO): string { return item.value; }
  trackBySectionId(index: number, item: { id: string }): string { return item.id; }
  trackByControl(index: number, control: any): any { return control; }
  trackByString(index: number, value: string): string { return value; }
  trackByOptionValue(index: number, option: BautagebuchOptionDTO): string { return option.value; }
  trackByFieldKey(index: number, field: BautagebuchOptionalFieldViewModel): string { return field.key; }
  trackByFieldName(index: number, field: FieldDefinitionDTO): string { return field.name; }
  trackByCompanyReport(index: number, report: BautagebuchCompanyReportViewModel): FormGroup { return report.reportControl; }
  trackByAssetId(index: number, item: { assetId: string }): string { return item.assetId; }
  trackByMarkerId(index: number, marker: FloorplanMarkerConfig): string { return marker.id; }
  trackByMediaId(index: number, media: MarkerLinkedMedia): string { return media.id; }

  private setupRegistergerichtFilter(field: FieldDefinitionDTO): void {
    if (!field.codeOptions) return;
    this.filteredOptions.next(field.codeOptions.slice());
    this.registergerichtFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => this.filterRegistergerichte(field.codeOptions!));
  }

  private filterRegistergerichte(options: CodeDTO[]): void {
    let search = this.registergerichtFilterCtrl.value;
    if (!search) { this.filteredOptions.next(options.slice()); return; }
    search = search.toLowerCase();
    this.filteredOptions.next(options.filter(opt => opt.state?.toLowerCase().includes(search) || opt.value?.toLowerCase().includes(search) || opt.postalCode?.toLowerCase().includes(search) || opt.label?.toLowerCase().includes(search)));
  }

  private getSchemaBaseUrl(): string { return `${BautagebuchLegacyFormComponent.BAUTAGEBUCH_API_BASE_URL}/report-definitions`; }
  private getMetadataUrl(messageType: string = this.messageType): string { return `${BautagebuchLegacyFormComponent.BAUTAGEBUCH_API_BASE_URL}/report-definitions/${messageType}/metadata`; }
  private getSectionsUrl(messageType: string = this.messageType): string { return `${BautagebuchLegacyFormComponent.BAUTAGEBUCH_API_BASE_URL}/report-definitions/${messageType}/sections`; }
  private getTemplatesUrl(messageType: string): string { return `${BautagebuchLegacyFormComponent.BAUTAGEBUCH_API_BASE_URL}/report-templates`; }
  private getGenerateXmlUrl(messageType: string = this.messageType): string { return `${BautagebuchLegacyFormComponent.BAUTAGEBUCH_API_BASE_URL}/report-xml`; }
  private getSaveDraftUrl(messageType: string = this.messageType): string { return `${BautagebuchLegacyFormComponent.BAUTAGEBUCH_API_BASE_URL}/report-drafts`; }
  private getDraftRequestOptions(): { headers?: HttpHeaders } { const organisationId = this.getRequiredOrganisationId(); return { headers: new HttpHeaders({ 'X-Organisation-Id': String(organisationId) }) }; }

  ngOnDestroy(): void { this.bautagebuchViewBindings$.next(); this.bautagebuchViewBindings$.complete(); this._onDestroy.next(); this._onDestroy.complete(); }

  private applyActiveOrganisation(organisation?: OrganisationInterface | null): void {
    if (!organisation) return;
    this.activeOrganisationId = organisation.organisationId || this.activeOrganisationId;
    this.activeOrganisationName = organisation.organisationName || organisation.name || this.activeOrganisationName;
  }

  private resolveFallbackOrganisation(): void {
    if (this.activeOrganisationId) return;
    const confirmedMemberships = this.getConfirmedMemberships();
    if (confirmedMemberships.length === 1) this.applyActiveOrganisation(confirmedMemberships[0]);
  }

  private getConfirmedMemberships(): OrganisationInterface[] { return (this.userService.currentMemberships || []).filter((membership): membership is OrganisationInterface => !!membership?.organisationId).filter((membership) => membership.membershipStatus === 'CONFIRMED'); }

  private setupWeatherFilter(): void {
    this.filteredWeatherOptions.next(this.weatherOptions.slice());
    this.weatherFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe((search) => {
      const normalized = (search || '').toLowerCase().trim();
      this.filteredWeatherOptions.next(!normalized ? this.weatherOptions.slice() : this.weatherOptions.filter(option => option.label.toLowerCase().includes(normalized)));
    });
  }

  private setupDataspaceConsumerFilter(): void {
    this.filteredDataspaceConsumerOrganisations.next(this.availableDataspaceConsumerOrganisations.slice());
    this.dataspaceConsumerFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe((search) => {
      const normalized = (search || '').toLowerCase().trim();
      this.filteredDataspaceConsumerOrganisations.next(!normalized ? this.availableDataspaceConsumerOrganisations.slice() : this.availableDataspaceConsumerOrganisations.filter((org) => `${org.organisationName || org.name || ''}`.toLowerCase().includes(normalized)));
    });
  }

  private getWorkerControl(reportIndex: number, workerIndex: number): FormGroup { return this.getWorkers(reportIndex).at(workerIndex) as FormGroup; }
  private setWorkerCustomField(worker: FormGroup, fieldName: 'qualifikation' | 'lohngruppe', enabled: boolean): void { const currentState = this.workerCustomFields.get(worker) || {}; currentState[fieldName] = enabled; this.workerCustomFields.set(worker, currentState); }

  private async saveToDatabase(closeAfterSave: boolean): Promise<void> {
    this.loading = true;
    const draftRequest: any = { messageType: this.messageType, sectionPath: this.sectionPath, fieldValues: this.form.value, caseId: this.caseId, destinationId: this.destinationId, serviceType: this.serviceType, serviceName: this.serviceName, draftId: this.draftId };
    if (this.inReplyToMessageId && this.inReplyToMessageType) { draftRequest.inReplyToMessageId = this.inReplyToMessageId; draftRequest.inReplyToMessageType = this.inReplyToMessageType; }

    this.http.post<any>(this.getSaveDraftUrl(), draftRequest, this.getDraftRequestOptions()).subscribe({
      next: (response) => {
        this.loading = false; this.draftId = response.draftId; this.initialFormValue = this.form.value; this.lastSavedResponse = response;
        this.snackBar.open('Entwurf erfolgreich gespeichert', 'Schließen', { duration: 2000 });
        if (closeAfterSave) { this.dialogRef.close(response); }
      },
      error: (error) => { this.loading = false; this.snackBar.open(`Fehler beim Speichern: ${error.message}`, 'Schließen', { duration: 5000, panelClass: ['error-snackbar'] }); }
    });
  }

  private showOverwriteWarning(): Promise<boolean> { const dialogRef = this.dialog.open(DialogConfirmComponent, { width: '400px', data: { title: 'Warnung', message: 'Bestehende Daten werden beim Speichern überschrieben. Fortfahren?', confirmText: 'Ja, speichern', cancelText: 'Abbrechen' } }); return dialogRef.afterClosed().toPromise(); }
  private showUnsavedChangesWarning(): Promise<boolean> { const dialogRef = this.dialog.open(DialogConfirmComponent, { width: '400px', data: { title: 'Ungespeicherte Änderungen', message: 'Sie haben ungespeicherte Änderungen. Möchten Sie wirklich schließen?', confirmText: 'Ja, schließen', cancelText: 'Abbrechen' } }); return dialogRef.afterClosed().toPromise(); }

  private async loadCurrentNUDataspaceAsset(): Promise<void> {
    const organisationId = this.getRequiredOrganisationId();
    if (!organisationId || !this.draftId) { this.nuDataspaceAsset = null; return; }
    try {
      const assets = await this.firstValue(this.bautagebuchService.getMyDataspaceAssets(organisationId));
      this.nuDataspaceAsset = assets.find(asset => asset.sourceSubmissionId === this.draftId) || null;
    } catch { this.nuDataspaceAsset = null; }
  }

  private hydrateNuPolicyStateFromTerms(terms?: any): void {}

  private deepMerge(target: any, source: any): any {
    if (!source || typeof source !== 'object') { return target; }
    if (!target || typeof target !== 'object') { return source; }
    const result = { ...target };
    Object.keys(source).forEach(key => {
      const targetValue = result[key];
      const sourceValue = source[key];
      if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
        result[key] = sourceValue; 
      } else if (typeof targetValue === 'object' && typeof sourceValue === 'object') {
        result[key] = this.deepMerge(targetValue, sourceValue);
      } else {
        result[key] = sourceValue;
      }
    });
    return result;
  }

  private withNuInternalStatus(fieldValues: any, status: string): any {
    return {
      ...(fieldValues || {}),
      [BautagebuchLegacyFormComponent.NU_UI_META_KEY]: {
        ...((fieldValues && fieldValues[BautagebuchLegacyFormComponent.NU_UI_META_KEY]) || {}),
        nuStatus: status,
        finalisedAt:
          status === BautagebuchLegacyFormComponent.NU_FINALISED_STATUS
            ? new Date().toISOString()
            : null
      }
    };
  }

  private normalizeImportedSources(sources: any[]): BautagebuchImportedSourceDto[] {
    /* REFACTORED NORMALIZE SOURCES FIX */
    return (sources || []).map((s): BautagebuchImportedSourceDto => ({
      assetId: s.assetId,
      producerOrgName: s.producerOrgName,
      projectKey: s.projectKey,
      reportDate: s.reportDate,
      selectedSectionIds: s.selectedSectionIds || [],
      workerSummary: s.workerSummary || [],
      workSummary: s.workSummary || []
    }));
  }

  private bindBautagebuchViewState(): void {
    this.form.valueChanges.pipe(takeUntil(this.bautagebuchViewBindings$)).subscribe(() => {
      this.refreshBautagebuchViewState();
    });
  }

  private refreshBautagebuchViewState(): void {
    this.refreshCompanyReportEntries();
    this.refreshActiveOptionalSections();
    this.refreshBautagebuchCatalog();
    this.refreshWeatherSummary();
    this.refreshProjectSummary();
  }

  private refreshBautagebuchCatalog(): void {
    if (!this.bautagebuchSections) { return; }
    const selected = new Set((this.form.get('selectedSectionIds')?.value || []) as string[]);
    const search = this.bautagebuchSearch.toLowerCase();
    this.bautagebuchOptionalCatalogGroups = this.bautagebuchSections.groups
      .filter(g => g.id !== 'core' && g.id !== 'intern')
      .map(g => ({
        ...g,
        sections: g.sections.filter(s => !selected.has(s.id) && s.title.toLowerCase().includes(search))
      }))
      .filter(g => g.sections.length > 0);
  }

  private refreshCompanyReportEntries(): void {
    this.companyReportEntries = (this.companyReports?.controls || []).map((control, index) => ({
      reportIndex: index,
      reportControl: control as FormGroup,
      workerControls: ((control as FormGroup).get('arbeitskraefte') as FormArray)?.controls || []
    }));
  }

  private refreshActiveOptionalSections(): void {
    if (!this.bautagebuchSections) { return; }
    const selected = new Set((this.form.get('selectedSectionIds')?.value || []) as string[]);
    const coreIds = new Set(['project', 'day', 'weather', 'companyReports']);
    this.bautagebuchActiveOptionalSections = this.bautagebuchSections.groups
      .reduce((acc, g) => acc.concat(g.sections), [] as BautagebuchSectionDTO[])
      .filter(s => !coreIds.has(s.id) && selected.has(s.id))
      .map(s => {
        const keys = this.getOptionalSectionKeys(s.id);
        const fields = keys.map(key => ({
          key,
          label: this.getOptionalFieldLabel(s.id, key),
          inputType: this.getOptionalInputType(s.id, key),
          options: this.getOptionalFieldOptions(s.id, key)
        }));

        return {
          id: s.id,
          title: s.title,
          previewFields: s.previewFields,
          entryCount: this.getNUSectionEntryCount(s.id),
          items: (this.form.get(s.id) as FormArray)?.value || [],
          fields
        };
      });
  }
  private refreshWeatherSummary(): void {
    const values = this.form.get('weather')?.value;
    if (!values) { return; }
    this.weatherSummaryTitle = values.tageslage || 'SONNIG';
    this.weatherSummaryMeta = `${values.temperaturMinC ?? 0}°C bis ${values.temperaturMaxC ?? 0}°C`;
    this.weatherSummaryIcon = this.getWeatherIcon(this.weatherSummaryTitle);
  }

  private getWeatherIcon(tageslage: string): string {
    switch (tageslage) {
      case 'REGEN': return 'umbrella';
      case 'BEWOELKT': return 'cloud';
      case 'SCHNEE': return 'ac_unit';
      case 'WINDIG': return 'air';
      default: return 'wb_sunny';
    }
  }

  private refreshProjectSummary(): void {
    const values = this.form.get('project')?.value;
    if (!values) { return; }
    this.projectSummaryTitle = values.bezeichnungDerBaumassnahme || 'Tagesbericht';
    this.projectSummaryMeta = values.auftraggeber?.name || '';
  }
}

/**
 * DTO interfaces matching backend structure
 */

interface FilteredMetadataDTO {
  messageType: string;
  sectionPath: string;
  fields: FieldDefinitionDTO[];
}

interface FieldDefinitionDTO {
  name: string;
  label: string;
  description?: string;
  type: string;
  required: boolean;
  codeListUrn?: string;
  codeOptions?: CodeDTO[];
  minOccurs?: string;
  maxOccurs?: string;
  isConditional?: boolean;
  conditionalField?: string;
  conditionalValue?: any;
  defaultValue?: any;
  readOnly?: boolean;   
  displayOnly?: boolean; 
}

interface CodeDTO {
  value: string;
  label: string;
  postalCode?: string;  
  state?: string;       
}

interface SimplifiedFormSubmissionDTO {
  messageType: string;
  sectionPath: string;
  fieldValues: { [key: string]: any };
  caseId?: string;
}

interface BautagebuchSectionsResponseDTO {
  messageType: string;
  defaultSectionIds: string[];
  groups: BautagebuchSectionGroupDTO[];
  defaultValues: {[key: string]: any};
}

interface BautagebuchSectionGroupDTO {
  id: string;
  title: string;
  sections: BautagebuchSectionDTO[];
}

interface BautagebuchSectionDTO {
  id: string;
  title: string;
  group: string;
  schemaPath: string;
  previewFields: string[];
  enabledByDefault: boolean;
  internal?: boolean;
}

interface BautagebuchTemplateDTO {
  id: string;
  messageType: string;
  name: string;
  data: {[key: string]: any};
  isSystem?: boolean;
}

interface BautagebuchOptionalFieldViewModel {
  key: string;
  label: string;
  inputType: string;
  options?: BautagebuchOptionDTO[];
}

interface BautagebuchOptionalSectionViewModel {
  id: string;
  title: string;
  previewFields: string[];
  entryCount: number;
  items: any[];
  fields: BautagebuchOptionalFieldViewModel[];
}

interface BautagebuchCompanyReportViewModel {
  reportIndex: number;
  reportControl: FormGroup;
  workerControls: any[];
}

interface BautagebuchOptionDTO {
  value: string;
  label: string;
}

interface BautagebuchSchemaSectionViewModel extends BautagebuchSectionDTO {
  fields: FieldDefinitionDTO[];
}
